/**
 * The client <-> server wire protocol (M9 Slice 1.1).
 *
 * This module pins the contract both sides import *before either side exists*.
 * It is deliberately socket-free: just the serializable message types plus a
 * pure {@link encode}/{@link decode} pair (JSON today) and the helpers that turn
 * a {@link World} into a wire snapshot and back. The actual transport (`ws`),
 * the server tick loop, and the browser read-model arrive in later slices and
 * build on exactly these shapes.
 *
 * Design notes that are load-bearing for later phases:
 *
 * - **Reuse over reinvention.** Client intent is the *existing* {@link Command}
 *   union verbatim - the sim's command queue is the server's authoritative seam,
 *   so the wire just wraps a `Command` with the sender's id. The world snapshot
 *   is built from the *existing* {@link serialize}/{@link deserialize} persistence
 *   format (the same one localStorage uses), so there is one serialization story,
 *   not two.
 *
 * - **Delta-ready from day one.** Phase 1 ships *full* snapshots every tick, but
 *   the snapshot keys entities by {@link EntityId} (a `Record`, not an array) so
 *   Phase 2's per-entity diffing is an additive change, not a protocol break.
 *   The map / RNG / id-allocator travel alongside so a fresh client can rebuild a
 *   live {@link World} from a single message.
 */

import type { MapData } from '../sim/map';
import type { Command } from '../sim/commands';
import type { ArenaDraftOption, ArenaFxEvent } from '../sim/arena';
import type { ArenaModifierId, ArenaProjectileKind } from '../sim/arenaWeapons';
import type {
  DungeonCastRejection,
  DungeonCombatView,
} from '../sim/dungeonCombat';
import type { AbilityShape } from '../sim/combat/abilityCatalog';
import type { ArenaHistoryPage } from '../arenaHistory';
import type { SplatRoll } from '../sim/blood';
import type {
  SphereEffect,
  SphereFizzleReason,
  SpherePlayerView,
} from '../sim/sphereDuel';
import type { Direction } from '../sim/direction';
import type { EntityId } from '../sim/entity';
import type { AchievementId, LeaderboardKey, TitleId } from '../sim/achievements';
import type { HeroStats } from '../sim/hero';
import type { CampaignTierId, Soulstone } from '../sim/arpg/soulstone';
import type { CampaignMoongateDestination } from '../sim/arpg/campaignTravel';
import type { ArpgItem } from '../sim/arpgItem';
import type { World } from '../sim/world';
import type { StarterRecipeId } from '../sim/cardContent';
import {
  serialize,
  deserialize,
  SCHEMA_VERSION,
  type WorldSnapshot as PersistedWorld,
  type EntitySnapshot,
} from '../sim/persistence';
import { encodeWorldMessage, decodeWorldMessage, isWorldMessageKind } from './binaryCodec';

/**
 * Identifies the sender of a {@link ClientCommandMessage}. A short opaque string
 * (assigned by the server on connect and echoed in {@link WelcomeMessage}); the
 * server maps it to the player's owned {@link EntityId} for the ownership check
 * that Slice 1.4 adds. Kept as a plain string so it is transport-agnostic.
 */
export type ClientId = string;

/**
 * Client -> server: a single piece of player intent. It wraps the *unchanged*
 * sim {@link Command} union and tags it with the sending {@link ClientId}. The
 * server validates the sender owns `command`'s referenced entity, then
 * {@link World.enqueue}s the wrapped command - intent reaches the authoritative
 * queue, and only via the queue.
 */
export interface ClientCommandMessage {
  kind: 'ClientCommand';
  clientId: ClientId;
  command: Command;
}

export interface ArenaHistoryRequestMessage {
  kind: 'ArenaHistoryRequest';
  entity: EntityId;
  before?: number;
  limit?: number;
}

export interface ArenaHistoryPageMessage extends ArenaHistoryPage {
  kind: 'ArenaHistoryPage';
}

/**
 * Client -> server: one **predicted** turn-in-place or step (UO-style per-step
 * movement). The client moves its local player immediately and sends exactly one
 * of these per turn/step start, keeping at most 5 unacked requests in flight; the
 * server answers each with a {@link MoveAckMessage} (applied verbatim) or a
 * {@link MoveRejectMessage} (denied - snap to the carried authoritative pose).
 * Unlike a {@link MoveIntentCommand}, which expresses a *held* heading the server
 * paces, a `Walk` is a single discrete action the client has already performed.
 *
 * Sequencing rules (pinned by tests):
 * - `seq` starts at 1 after a Welcome and wraps 255 -> 1; 0 is never sent (the
 *   server reserves `seq: 0` on a reject for a server-initiated reposition).
 * - On a `MoveReject` the client clears its pending queue, snaps to the carried
 *   pose, adopts the carried `epoch`, and resets `seq` to 1.
 * - The server bumps its session epoch after any reject and silently drops
 *   (never re-rejects) walks carrying a stale epoch, so the requests that were
 *   already in flight when a reject fired cannot cause a reject storm.
 */
export interface WalkRequestMessage {
  kind: 'Walk';
  /** Per-step sequence number: 1..255, wraps 255 -> 1; 0 is never sent. */
  seq: number;
  /** Echoed from the last {@link MoveRejectMessage} (0 initially); guards stale in-flight requests. */
  epoch: number;
  /** The action's facing: the new facing for a turn, the step direction for a step. */
  dir: Direction;
  /** Whether a step uses the run gait (its cost/duration); ignored for a turn. */
  running: boolean;
  /**
   * The client's own turn-vs-step classification (ADR-0248): `true` = a step onto
   * the `dir` neighbour, `false` = a turn-in-place. The server applies exactly
   * this, never inferring the kind from its own facing - a server-side cosmetic
   * facing write (the arena cast turn, ADR-0192) between two walks must not
   * downgrade an in-flight step to a turn and silently strand the authority a
   * tile behind the prediction. Optional only for one deploy window's worth of
   * already-loaded clients: when absent the server falls back to the legacy
   * `dir !== facing` inference.
   */
  step?: boolean;
}

/**
 * Client -> server: one dungeon-only continuous locomotion intent change.
 *
 * This is deliberately separate from Town/Arena `Walk`. The server integrates
 * the held direction in the private dungeon world until another sequence-numbered
 * change arrives, while the client runs the same model immediately.
 */
export interface DungeonMoveIntentMessage {
  kind: 'DungeonMoveIntent';
  seq: number;
  dir: Direction | null;
  running: boolean;
  turnOnly: boolean;
  /** Exact simulated duration of the intent this edge replaces. */
  previous?: {
    seq: number;
    elapsedMs: number;
  };
}

/** Server -> client: the step/turn request `seq` was applied verbatim. */
export interface MoveAckMessage {
  kind: 'MoveAck';
  /** The {@link WalkRequestMessage.seq} this ack answers. */
  seq: number;
}

/**
 * Server -> client: an **authoritative reposition**. `seq > 0` means the walk
 * request with that seq was denied (blocked tile, bad seq, too fast) - the client
 * clears its pending queue and snaps to the carried pose. `seq === 0` is a
 * server-initiated reposition with no request to blame (a teleporter landing, a
 * resync). Either way the client adopts the carried `epoch` and resets its seq
 * counter to 1; the server drops any still-in-flight walks under the old epoch.
 */
export interface MoveRejectMessage {
  kind: 'MoveReject';
  /** The denied request's seq, or 0 for a server-initiated reposition. */
  seq: number;
  /** The fresh session epoch the client must echo on every subsequent walk. */
  epoch: number;
  x: number;
  y: number;
  z: number;
  facing: Direction;
}

/**
 * Server -> client: the world state as of a tick, **keyed by {@link EntityId}**
 * for delta-readiness. The entity payloads are the persistence
 * {@link EntitySnapshot}s verbatim; `map`, `rngState`, and `idCounter` travel
 * alongside so a single snapshot is enough to (re)build a live {@link World}
 * (see {@link snapshotToWorld}).
 *
 * Phase 1 sends this in full every tick. Phase 2 will send a diff with the same
 * shape (a subset of `entities`, plus a set of removed ids), so consumers that
 * read `entities[id]` keep working unchanged.
 */
export interface WorldSnapshotMessage {
  kind: 'Snapshot';
  /**
   * The server's save-schema version ({@link SCHEMA_VERSION}), carried on the wire
   * so a stale client can detect a compatibility-breaking deploy (Issue #320).
   * Optional and additive: an older server that predates this field sends nothing,
   * and {@link snapshotToWorld} then assumes the local version (no throw). When a
   * newer server bumps `SCHEMA_VERSION`, this differs from the old client's constant
   * and `deserialize` throws `Incompatible save version`, which {@link
   * import('./gameClient').GameClient.receive} now catches and degrades to the reload
   * flow instead of stranding the player.
   */
  version?: number;
  /** This project's own map format (palette + cells + walkability + statics). */
  map: MapData;
  /** The seeded RNG's internal state, so a rebuilt world resumes the stream in place. */
  rngState: number;
  /** The id the rebuilt world's next allocation will return. */
  idCounter: number;
  /** Every entity, keyed by its {@link EntityId} (string keys after JSON). */
  entities: Record<EntityId, EntitySnapshot>;
  /** Pending (undrained) commands, in FIFO order. */
  commandQueue: Command[];
}

/**
 * Client -> server, the **first** frame a connection sends (Lightweight Accounts
 * Slice A). Identity used to ride the connect URL's query string; it now travels
 * here instead, so the server can validate a name *before* spawning and **reject**
 * a reserved-name collision (the one ADR-0004 exception) - and so a passcode
 * (Slice B) never lands in a URL/access log (see ADR-0038). The socket opens
 * without identity; the server spawns nothing until this arrives, then answers
 * with either a {@link WelcomeMessage} (spawned) or a {@link RejectedMessage}
 * (closed). The `name`/`sex`/`hue` fields mirror the naked-start splash's creation
 * form (Cosmetic Progression Slice C, ADR-0044); `sex`/`hue` are the **immutable
 * body identity** a fresh or legacy reservation persists on first claim.
 */
export interface LoginMessage {
  kind: 'Login';
  /** The chosen display name (may be blank; the server falls back to a default). */
  name: string;
  /** The chosen UO body id: 400 (male) or 401 (female). */
  sex?: number;
  /** The chosen 1-based UO skin hue id (0/absent => the server's cycled default). */
  hue?: number;
  /** False for the credential probe, true after the creation controls were shown. */
  appearance?: boolean;
  /**
   * Compact cosmetic loadout `<id>:<hue>,…`; **no longer sent by the naked-start
   * splash** (Cosmetic Progression Slice C, ADR-0044) - a reserved account spawns in
   * its persisted, server-authoritative `equippedOutfit` instead. Retained on the
   * wire only for an anonymous session at login (`coerceEquipment`); the wardrobe
   * dresses through the separate {@link EquipMessage} instead (Slice E, ADR-0050),
   * not this field. Omitted entirely for a bare body.
   */
  equip?: string;
  /**
   * The passcode that proves ownership of a reserved name (Lightweight Accounts
   * Slice B). A non-blank value claims the name on first use (the server salts +
   * scrypt-hashes it) and authenticates the owner on every later login; **blank or
   * omitted means "don't reserve"** - an anonymous, unreserved session as in Slice
   * A. It rides this **message body**, never the connect URL, so it stays out of
   * access logs, proxy logs, and browser history (ADR-0038/ADR-0039). The server
   * never echoes it back and never persists it - only the salt + hash (ADR-0039).
   */
  passcode?: string;
}

/**
 * Client -> server: open a **read-only spectator** view (login-screen backdrop).
 * Unlike a {@link LoginMessage} it spawns **no entity** and reserves no name - the
 * socket is simply added to the broadcast set so the server streams it the live
 * world (a keyframe, then per-tick deltas) for rendering behind the splash. There
 * is nothing to attribute to it: a spectator sends no commands and no speech, so a
 * spectator socket never owns a character and never appears in the world to anyone
 * else. The real {@link LoginMessage} rides a separate connection when the visitor
 * enters; the spectator socket is closed once they do.
 */
export interface SpectateMessage {
  kind: 'Spectate';
}

/**
 * Server -> client: the login was refused, so **no entity was created** and the
 * socket is about to close (Lightweight Accounts Slice A). The refusal cases are a
 * reserved name presented with a wrong passcode (Slice B), or an un-credentialed
 * reservation (a passcode-less legacy record, or one an admin cleared via
 * `/resetpass`) claimed with a *blank* passcode - a non-blank passcode re-claims it
 * instead (admin-mediated recovery, ADR-0084). `reason` is a
 * human-readable message the splash shows so the visitor can fix the passcode, edit
 * the name, and retry. This is the deliberate, documented exception to ADR-0004's
 * never-reject identity contract (see ADR-0038/ADR-0039).
 */
export interface RejectedMessage {
  kind: 'Rejected';
  /** Why the login was refused, shown to the user on the splash. */
  reason: string;
}

/** Server -> client: valid credentials, but this character still needs an appearance. */
export interface CharacterRequiredMessage {
  kind: 'CharacterRequired';
}

/**
 * Server -> client, sent once on connect: the {@link EntityId} the server
 * spawned for this client, its {@link ClientId}, and an initial full
 * {@link WorldSnapshotMessage} to seed the client's read-model. Every subsequent
 * tick the server sends a bare {@link WorldSnapshotMessage}.
 */
export interface WelcomeMessage {
  kind: 'Welcome';
  clientId: ClientId;
  entityId: EntityId;
  snapshot: WorldSnapshotMessage;
  /** Server-authoritative launch gate for the unreleased Trading Card game. */
  cardsEnabled?: boolean;
  /**
   * The server process's build id (Issue #320), baked from the same source as the
   * client's `VITE_BUILD_ID` (a git short SHA) so a client can tell it is running
   * pre-patch code. Because a fast redeploy only blips the socket (the page never
   * reloads), the client compares this to its own baked build id on **every**
   * reconnect's Welcome and offers/forces a refresh onto the new bundle when they
   * differ. Optional and additive: `undefined` when the server has no build id
   * configured (dev, or a deploy that didn't thread it), in which case the client
   * never prompts (the compare silently no-ops).
   */
  buildId?: string;
  /**
   * When `true`, a build-id mismatch reloads the client **without** waiting for a
   * click (Issue #320) - the escape hatch for a compatibility-breaking deploy (e.g.
   * a `SCHEMA_VERSION`/wire change that makes the old client genuinely incompatible).
   * Force-vs-optional is a per-deploy, server-controlled decision (the server sets it
   * from its `FORCE_RELOAD` env), never hardcoded on the client. Defaults to a
   * non-blocking prompt (`false`/absent) so a mid-duel/mid-storm player is not yanked.
   */
  forceReload?: boolean;
  /**
   * True iff this connection's passcode-proven `accountKey` is a configured admin
   * (`isAdmin`, ADR-0183 - the same gate the chat admin verbs use). The client uses
   * it **only** to reveal the admin UI (wire the F10 panel, arm the admin-teleport
   * key); it grants **no power** - the server re-authorizes every admin command
   * against the socket's own proven identity, so a forced-open panel on a non-admin
   * still accomplishes nothing. Optional/additive: absent means "not admin", so an
   * older client that ignores it simply never shows the UI.
   */
  isAdmin?: boolean;
  profile?: ProfileState;
}

export interface WireAchievementEvidence {
  board?: LeaderboardKey;
  season?: number;
  rank?: number;
  score?: number;
  /** The durable counter a counter-threshold grant read (PRD #669). */
  counter?: string;
  /** The counter's value at the write that crossed the threshold. */
  value?: number;
}

export interface WireEarnedAchievement {
  id: AchievementId;
  earnedAt: number;
  evidence?: WireAchievementEvidence;
}

export type ProfileState =
  | { reserved: false; achievements: []; selectedTitleId: null }
  | {
      reserved: true;
      achievements: WireEarnedAchievement[];
      selectedTitleId: TitleId | null;
    };

export interface SelectTitleMessage {
  kind: 'SelectTitle';
  entity: EntityId;
  titleId: string | null;
}

export interface ProfileStateMessage {
  kind: 'ProfileState';
  profile: ProfileState;
}

/** One fresh, owner-only grant. Unlike ProfileState, this is never replayed on login. */
export interface AchievementAwardMessage {
  kind: 'AchievementAward';
  achievement: WireEarnedAchievement;
}

/**
 * Server -> client: several owner-only cues delivered in **one** frame (PRD
 * #825).
 *
 * A dense Arena tick emits a burst of independent ephemeral messages - a die
 * clip, a damage float, a blood splatter, an FX event, the run status - one per
 * enemy involved. Sent individually that was measured at ~114 `JSON.stringify`
 * plus `socket.send` pairs per second per run: 114 wire frames, 114 framing
 * headers, and (since permessage-deflate) 114 separate deflate jobs, for a burst
 * that is a single logical update. Staged and shipped together they cost one of
 * each, and the shared vocabulary across the batch is exactly what a compressor
 * eats for free.
 *
 * The envelope carries **no semantics**: the receiver dispatches each `cues`
 * entry exactly as if it had arrived on its own, in order, so batching is
 * invisible to every handler ({@link GameClient.receive}). Only ephemeral,
 * off-snapshot cues ride here - never a world message, whose binary encoding
 * this JSON envelope could not carry.
 */
export interface CueBatchMessage {
  kind: 'CueBatch';
  cues: ServerMessage[];
}

/**
 * Server -> client: a per-broadcast **delta** against a baseline the client already
 * holds (M9 Slice 2.2; sent on the broadcast cadence, 10Hz by default since ADR-0163).
 * This is the bandwidth win: instead of re-sending every
 * entity each broadcast, the server sends only what *changed* since the client's last
 * sent/acked state - the entities that appeared (`added`), the ones whose
 * snapshot bytes differ (`changed`), and the ids that vanished (`removed`) - all
 * keyed by {@link EntityId}, exactly the shape Slice 1.1 made the full snapshot
 * delta-ready for.
 *
 * The tick-volatile scalars (`rngState`, `idCounter`) and the pending
 * `commandQueue` are small and travel in full every delta, so applying a delta
 * reconstructs a complete {@link WorldSnapshotMessage}. The `map` is **not**
 * carried: it is static for the life of a world, so the client keeps the one from
 * its connect-time map-bearing frame (its {@link WelcomeMessage} or seeding
 * {@link WorldSnapshotMessage}) and carries it forward - exactly as it does across a
 * map-less {@link WorldKeyframeMessage} (issue #119).
 *
 * A client must hold a baseline (from a {@link WelcomeMessage} or a
 * {@link WorldSnapshotMessage}/{@link WorldKeyframeMessage}) before a delta can be
 * applied; the server guarantees this by sending the map-bearing
 * {@link WorldSnapshotMessage} on connect (in the `Welcome`/spectator seed, before
 * any tick broadcast), then a keyframe to any connection it has no recorded baseline
 * for yet and periodically thereafter.
 */
/**
 * The component-field keys of an {@link EntitySnapshot}, excluding the immutable
 * `id` (which is the map key of a delta entry, never restated). Deriving this from
 * `EntitySnapshot` keeps the field-delta machinery **generic over the component
 * set**: adding a new optional component to `EntitySnapshot` (e.g. a future storm
 * marker) automatically flows through {@link diffEntity}/{@link applyEntityDelta}
 * with no per-component wiring - {@link diffEntity} iterates the entity's actual
 * keys, so an unknown component diffs like any other.
 */
export type EntityComponentKey = Exclude<keyof EntitySnapshot, 'id'>;

/**
 * A per-entity **field-level** delta (ADR-0164): for an entity present in both the
 * baseline and this tick, only the component fields that actually changed. This is
 * the byte win over the old entity-level delta - a rat scurrying re-sends only its
 * `position` + `mobile` pose, not its unchanged `renderable`/`rat` (which used to
 * ride every frame).
 *
 * - `set`: each component whose value changed **or** was newly added since the
 *   baseline, carried by its full new value. Absent when nothing was set/added.
 * - `cleared`: component keys present on the baseline entity but **gone** this tick
 *   (a component removed from a surviving entity - e.g. `death` cleared on
 *   resurrect). Absent when nothing was removed. Load-bearing for correctness: a
 *   component simply *absent* from `set` means "unchanged", so a removed component
 *   must be named explicitly, exactly as a despawned entity is named in `removed`.
 *
 * The entity's `id` is the map key in {@link WorldDeltaMessage.changed}, never
 * restated here. An entity whose fields are all unchanged is omitted from `changed`
 * entirely ({@link diffEntity} returns `null`).
 */
export interface EntityFieldDelta {
  /** Components changed or newly added since the baseline, each by full new value. */
  set?: Partial<Omit<EntitySnapshot, 'id'>>;
  /** Component keys present on the baseline entity but removed this tick. */
  cleared?: EntityComponentKey[];
}

export interface WorldDeltaMessage {
  kind: 'Delta';
  /** The seeded RNG's internal state as of this tick (small; sent in full). */
  rngState: number;
  /** The id the world's next allocation will return, as of this tick. */
  idCounter: number;
  /** Entities present this tick that were absent in the baseline, keyed by id. */
  added: Record<EntityId, EntitySnapshot>;
  /**
   * Entities present in both the baseline and this tick whose bytes changed, keyed
   * by id, each carried as a **field-level** {@link EntityFieldDelta} - only the
   * changed/removed components, not the whole snapshot (ADR-0164).
   */
  changed: Record<EntityId, EntityFieldDelta>;
  /** Ids present in the baseline but gone this tick (despawned/disconnected). */
  removed: EntityId[];
  /** Pending (undrained) commands, in FIFO order (small; sent in full). */
  commandQueue: Command[];
}

/**
 * Server -> client: a periodic **full resync** of the live world that omits the
 * static `map` (issue #119). It is the {@link WorldSnapshotMessage}'s authoritative
 * twin - the *complete* entity set keyed by {@link EntityId}, plus the tick scalars
 * and command queue - but **without** the immutable map, which the client already
 * holds from its connect-time keyframe and carries forward exactly as it does for a
 * {@link WorldDeltaMessage}. The map is ~99% of a full snapshot's bytes and never
 * changes for the life of a world, so re-sending it once per second in every keyframe
 * dominated egress (~1.78 MB/s per connection) and bound the single-VM ceiling at the
 * link's bandwidth. A connection receives the map **once** - in its {@link WelcomeMessage}
 * (a player) or its seeding `Snapshot` (a spectator) - and every periodic keyframe
 * thereafter is this map-less frame instead.
 *
 * Unlike a delta (which sends only what *changed*), a keyframe carries the **whole**
 * entity set, so the client replaces its read-model wholesale - it is a true resync
 * that self-corrects any drift, exactly like the old full keyframe, just without the
 * redundant map. Applying it onto the held baseline (for the map) reconstructs a
 * complete {@link WorldSnapshotMessage}, so the read-model has one reconstruction
 * path whether fed a keyframe, a delta, or a Welcome. A client must hold a baseline
 * before one can be applied; the server guarantees this by sending the full map-bearing
 * frame on connect, before any periodic keyframe.
 */
export interface WorldKeyframeMessage {
  kind: 'Keyframe';
  /** The seeded RNG's internal state as of this tick (small; sent in full). */
  rngState: number;
  /** The id the world's next allocation will return, as of this tick. */
  idCounter: number;
  /** Every entity this tick, keyed by its {@link EntityId} - the complete set, not a diff. */
  entities: Record<EntityId, EntitySnapshot>;
  /** Pending (undrained) commands, in FIFO order (small; sent in full). */
  commandQueue: Command[];
}

/**
 * Client -> server: a line of chat the player typed (Slice 5). Carries text and
 * an optional untrusted TCG card reference, but deliberately no sender field.
 * Speech is attributed exactly like a {@link ClientCommandMessage}: the server
 * stamps the speaker from the
 * *connection's* owned entity (name/hue/entity-id read off its renderable),
 * never from the wire, so a claimed sender cannot be spoofed (mirroring the
 * command-ownership rule). It is an **ephemeral** event, not world state: it
 * rides this dedicated message pair, never the snapshot/delta (see ADR-0010).
 */
export interface SayMessage {
  kind: 'Say';
  /** The line the player typed. The server caps its length before broadcasting. */
  text: string;
  /**
   * Zero or more TCG cards attached to their visible `[Card Name]` tokens. The
   * server accepts each only when the sender owns that concrete rarity, so a line
   * can link any number of owned cards. Absent for a card-free line.
   */
  cards?: WireCardLink[];
}

/**
 * Client -> server: choose the hue the player's **own** speech text renders in
 * (overhead and in the journal). This is a per-connection cosmetic preference,
 * not world state and not identity: it colours only the sender's own words, so -
 * unlike the spoof-proof {@link SayMessage} - it is safe for the client to carry.
 * The server keeps it on the session and stamps it onto every later
 * {@link SpeechMessage} the connection produces. `0` means "no hue" - the UO
 * default of plain white (the historical hue-0 look). The setting is **ephemeral
 * and session-scoped**: it is never persisted server-side, so a client restores
 * its own choice (from local storage) by re-sending this after each Welcome.
 */
export interface SetSpeechHueMessage {
  kind: 'SetSpeechHue';
  /** The chosen 1-based UO hue id for the sender's own speech; `0` => default white. */
  hue: number;
}

/**
 * The fixed text of the private cosmetic-award emote (Cosmetic Progression Slice
 * B, PRD #101 / ADR-0048). Lives here in the shared protocol so both the server
 * (which emits it) and the client (which sounds the level-up cue on it, see
 * `awardCue`) agree on the one source of truth - the award emote is the *only*
 * action emote that plays the reward jingle, distinguishing it from other
 * broadcast action emotes such as the dice roll (ADR-0083).
 */
export const AWARD_EMOTE_TEXT = 'New Cosmetic Award!';

/**
 * The display name the server stamps onto system **herald** lines - the `/top`
 * standings reply and the Rat Swarm announcements (ADR-0043/ADR-0119) - so they
 * read as the bank announcing them rather than a player speaking. Lives in the
 * shared protocol so the client can tell a herald from real player speech: the
 * "bank" easter-egg cue (`bankCue`) fires only on player chatter, never on a
 * herald line (which would otherwise trigger it, since the swarm-start herald
 * literally names the bank).
 */
export const LEADERBOARD_HERALD_NAME = 'Bank Herald';

/**
 * The stamp name the server puts on the red **"OPPONENT is attacking you!"**
 * overhead line the moment a duel opens (Player duels, PRD #252, ADR-0131). It
 * rides the ordinary {@link SpeechMessage} shape, but the server sends it to
 * **only the victim's own socket** over their own entity (private to that
 * duelist, never broadcast to the room), and the client recognises this name and
 * draws the line in combat red and keeps it out of the durable journal - the same
 * "stamp identifies a system line" trick the herald name uses. The overhead
 * surface never shows the stamp name (only the text), so this never leaks into
 * what a viewer reads. (The combat *stance* stays replicated over the snapshot,
 * so onlookers still read that a duel is under way - only this text is private.)
 */
export const COMBAT_HERALD_NAME = 'Combat';

/**
 * Server -> client: one spoken line, broadcast to **every** connection the
 * instant the server accepts a {@link SayMessage} (Slice 5). It carries the line
 * plus the speaker's identity - all stamped server-side from the speaker's
 * entity, so the client can render fading overhead text in the speaker's hue and
 * (for player speech) append a journal line attributed to them - ambient NPC
 * barks still float overhead but are kept out of the journal (see {@link npc},
 * ADR-0075). There is no speech-range system: everyone present receives every
 * line (the crop is tiny).
 *
 * Ephemeral by design: like {@link MoveAckMessage} it is its own tiny message and
 * is **never** part of the world snapshot/delta - speech has no place in
 * authoritative, persisted world state (see ADR-0010).
 */
export interface SpeechMessage {
  kind: 'Speech';
  /** The speaking entity, so the client routes the overhead text to its sprite. */
  entityId: EntityId;
  /** The speaker's display name (from its renderable), for the journal line. */
  name: string;
  /**
   * The 1-based UO hue id the speaker's text renders in. For a player this is
   * their chosen {@link SetSpeechHueMessage} preference (default `0` => white, the
   * historical hue-0 look); for an NPC bark it is the entity's own body hue.
   */
  hue: number;
  /** The spoken line (already length-capped by the server). */
  text: string;
  /**
   * Stable server-stamped identity for player-authored journal lines.
   * Moderation retractions target this key instead of presentation text.
   */
  authorKey?: string;
  /**
   * Whether this line is an **action/emote**, not ordinary speech (Cosmetic
   * Progression, PRD #101). An action renders in the classic UO emote style - the
   * text wrapped in `*asterisks*` and drawn yellow - rather than a plain coloured
   * chat line, so the system "New Cosmetic Award!" emote reads as an action over the
   * earner's head, not as something they said. Absent/`false` => ordinary speech, so
   * every existing `Speech` (player chat, NPC barks, the `/top` herald) is unchanged.
   */
  action?: boolean;
  /**
   * Whether this action line was **authored by the speaking player** rather than by
   * the server (ADR-0387): today the chat-triggered emote (`*dances*`), where the
   * player typed the words themselves and the server merely restyled them.
   *
   * The discriminator the per-viewer ignore filter needs. An `action` line is normally
   * unignorable ({@link ../ignoreList.shouldSuppressSpeech}) because every other one is
   * a *system* line - the `/top` standings herald, the combat "attacking you!" alert,
   * the cosmetic award emote, the dice-roll emote, the duel-count float, a moderator
   * notice - which a viewer must never be able to hide. A player-authored one is
   * ordinary player chat wearing emote styling, so it falls through to the usual
   * ignore-by-name gate: `/ignore <name>` hides a dance spammer's line like any other.
   * Absent/`false` => the historical system-line behaviour, so every existing action
   * line is unchanged.
   */
  playerAction?: boolean;
  /**
   * Whether this line is **overhead-only**, not durable speech: an **ambient NPC bark**
   * from the socket-less roster ({@link stepNpcSpeech}), or - since ADR-0145/0387 - a
   * player *gesture* float (`*moonwalks*`, the `*dances*` chat emote), which the room
   * watches rather than reads back. The server stamps it at the emission point, never on
   * ordinary player chat, the `/top` herald, or the award emote.
   * The client still floats every line overhead, but gates the persistent journal
   * on `!npc` so the log shows only real player speech (ADR-0075). Backward-
   * compatible: absent/`false` => treated as player speech, the historical
   * everything-is-journalled behaviour.
   */
  npc?: boolean;
  /**
   * Whether this line should land in the **chat journal only**, with no overhead
   * bubble floated over `entityId` (Unified Arena/bank chat). Player chat is one
   * server-wide channel that crosses between the bank and every private Arena
   * instance, but Arena run worlds reuse town entity ids (ADR-0176), so a
   * cross-world overhead would float over the wrong actor in the recipient's
   * view. The speaker's *own* world receives an ordinary `Speech` (overhead +
   * journal); every other socket receives this `journalOnly` copy so the text
   * still reaches the log without misrendering. Absent/`false` => the historical
   * float-and-journal behaviour, so every non-chat `Speech` is unchanged. A say line
   * that fired a **chat emote** (ADR-0387) is the exception: it is a gesture the room
   * watches and is journal-excluded anyway, so it goes to its own world only and no
   * `journalOnly` copy is sent at all.
   */
  journalOnly?: boolean;
  /** Server-validated TCG cards attached to their visible chat tokens (any number). */
  cards?: WireCardLink[];
}

/** Server -> client: remove one author's retained lines from the local chat journal. */
export interface ChatHistoryRemoveMessage {
  kind: 'ChatHistoryRemove';
  authorKey: string;
}

/**
 * The **one** definition of "this line is the private cosmetic-award emote". The
 * award emote is the sole `action` line whose text equals {@link AWARD_EMOTE_TEXT};
 * every other `action` line (the `/top` herald, the dice-roll emote, ADR-0083) has
 * different text, and ordinary speech is not `action` at all. Three client surfaces
 * key off this single predicate so they can never drift apart: it still floats
 * overhead and still sounds the level-up cue ({@link awardCue}), but it is kept out
 * of the durable journal ({@link shouldJournalSpeech}, ADR-0126).
 */
export function isAwardEmote(speech: SpeechMessage): boolean {
  return speech.action === true && speech.text === AWARD_EMOTE_TEXT;
}

/**
 * Whether an inbound {@link SpeechMessage} belongs in the **durable journal** (the
 * persistent chat log), as opposed to the transient overhead bubble, which shows
 * every line. The journal is real player chat only: ambient NPC-roster barks and the
 * player gesture floats (`*moonwalks*`, the `*dances*` chat emote, ADR-0145/0387) are
 * excluded via their server-stamped {@link SpeechMessage.npc} flag (ADR-0075), and
 * the private, self-only cosmetic-award emote is excluded too (ADR-0126) — it still
 * floats overhead and cues the level-up sound, it just does not clutter the log.
 * Other heralds/emotes (the `/top` reply, the dice roll) remain journalled.
 */
export function shouldJournalSpeech(speech: SpeechMessage): boolean {
  return !speech.npc && !isAwardEmote(speech);
}

/**
 * Server -> client: play a body animation clip on an entity, in a given facing -
 * the first non-locomotion animation broadcast (Rat Swarm Slice B, PRD
 * #222 / ADR-0116). The stomper's attack swing and the rat's death clip both ride
 * this one message: the server broadcasts it at the instant of the event, every
 * client plays the named clip through on that entity's pooled sprite and
 * returns it to standing - once by default, or {@link ActionAnimationMessage.repeat}
 * times for a repeating gesture like the dance emote (ADR-0387).
 *
 * Ephemeral by design, exactly like {@link SpeechMessage}: an animation moment is
 * an *event*, never world state, so it rides its own tiny message and is never
 * part of the snapshot/delta (a late joiner correctly sees nothing - there is
 * nothing to catch up). `clip` is a clip name from the body's baked set (e.g.
 * `attack`, `die`); a client that has no such clip for the target's body - an
 * unknown name, unbaked art - treats the message as a safe no-op, so the wire
 * format never gates on which clips a client managed to load.
 */
export interface ActionAnimationMessage {
  kind: 'ActionAnimation';
  /** The entity whose sprite plays the clip. Unknown to this client => no-op. */
  entityId: EntityId;
  /** The baked clip name to play (e.g. `attack`, `die`). Unknown => no-op. */
  clip: string;
  /** The facing the clip plays in (the swing aims at the target). */
  facing: Direction;
  /**
   * How many times to play the clip through before returning to standing (the dance
   * emote, ADR-0387). Absent / `<= 1` is the historical one-shot, so every existing
   * sender and any older client stays byte-for-byte unchanged; the dance broadcast
   * carries {@link ../sim/content.DANCE_REPEAT}. Still one broadcast per gesture - the
   * loop is the receiving sprite's, so a repeat costs no extra wire traffic and stays
   * as ephemeral (and as cancellable) as a single pass.
   */
  repeat?: number;
  /**
   * Optional idempotency key shared by a predicted action and its authoritative
   * echo. The renderer uses it to present the action immediately without replaying
   * the same clip when the server response arrives.
   */
  actionId?: string;
}

/**
 * One row of the live Rat Swarm event board (PRD #222 / ADR-0119): a stomper's
 * display name and their kill count this event. Anonymous and reserved hunters
 * appear alike - the board is keyed by display name on the wire because the
 * session key is server bookkeeping a viewer has no use for.
 */
export interface SwarmTallyEntry {
  /** The stomper's display name (reserved or anonymous alike). */
  name: string;
  /** Rats this stomper has killed this event. */
  kills: number;
}

/**
 * Server -> client: the Rat Swarm event's live status (PRD #222 / ADR-0119),
 * broadcast to every connection **when it changes** - on start, on every kill,
 * and at the end (extinction or timeout) - plus once to a socket that logs in
 * mid-event, so a late joiner's board is seeded. Slice F's event overlay renders
 * entirely from these pushes.
 *
 * Ephemeral like {@link SpeechMessage}: event state is server memory beside the
 * world (ADR-0117), never snapshot state, so it rides its own tiny message.
 * `ticksRemaining` counts the server's 50ms steps (`STEP_MS`); the client may
 * extrapolate a smooth countdown between pushes and snap to each fresh one (the
 * {@link OnlineTimeMessage} honesty bound). `tallies` is every non-zero scorer,
 * best first (kills desc, first-to-reach tiebreak - the winner-selection order),
 * and is structurally bounded: only killers appear, so it can never exceed the
 * event's rat count (fifty).
 */
export interface RatSwarmStatusMessage {
  kind: 'RatSwarmStatus';
  /** Whether the event is running. `false` on the final (end-of-event) push. */
  active: boolean;
  /** Rats still alive (stompable) in the bank. */
  ratsRemaining: number;
  /** Server ticks (50ms) left before the timeout; `0` once the event has ended. */
  ticksRemaining: number;
  /** Every non-zero tally, best first. Empty until the first kill. */
  tallies: SwarmTallyEntry[];
}

/**
 * Server -> client: Blackthorn's Storm live status (PRD #289 Slice D / ADR-0151), the
 * {@link RatSwarmStatusMessage} sibling. Broadcast to every logged-in connection **when it
 * changes** - Blackthorn's arrival, each summon, each fall, and the end - plus once to a
 * socket that logs in mid-storm, so a late joiner's board is seeded. Slice F's event
 * overlay renders entirely from these pushes; the `active` flip is also what drives the
 * client-side storm-start growl cue (Slice E's sound observer, the swarm-cue precedent).
 *
 * Ephemeral like {@link SpeechMessage}: storm state is server memory beside the world,
 * never snapshot state, so it rides its own tiny message. `ticksRemaining` counts the
 * server's 50ms steps of the 2:00 combat clock (the full clock while Blackthorn is still
 * heralding, then counting down); the client may extrapolate a smooth countdown between
 * pushes. `living`/`fallen` are the enrolled-and-present tallies (a glanceable count, not a
 * roster - the per-name death feed was dropped as clutter, ADR-0156).
 */
export interface StormStatusMessage {
  kind: 'StormStatus';
  /** Whether the storm is running. `false` on the final (end-of-event) push. */
  active: boolean;
  /** Server ticks (50ms) of the 2:00 combat clock remaining; `0` once the storm has ended. */
  ticksRemaining: number;
  /** Energy vortexes currently alive and hunting. */
  vortexCount: number;
  /** Enrolled players still connected and on their feet (Mark-eligible survivors so far). */
  living: number;
  /** Players the storm has downed this event (the mass-resurrection targets). */
  fallen: number;
}

/**
 * Server -> client: an ephemeral telegraph that an energy vortex will materialize on a
 * ground tile in ~2s (PRD #289 Slice D / ADR-0151). Broadcast to every logged-in
 * connection the instant Blackthorn telegraphs an escalation summon; Slice E renders the
 * danger marker on `(x, y)` and lets it decay. Like {@link ActionAnimationMessage} it is a
 * one-shot moment, never snapshot state, so it rides its own tiny message and the client
 * may drop it without consequence.
 */
export interface StormTelegraphMessage {
  kind: 'StormTelegraph';
  /** Absolute map tile the vortex will appear on. */
  x: number;
  y: number;
  /** Server ticks (50ms) until the vortex materializes, so the marker can count down. */
  ticksUntil: number;
}

/**
 * Server -> client: an ephemeral smoke-poof cue of The King's Justice (PRD #361 /
 * ADR-0194) - the guard ritual's `0x3728` puff at a tile/level, fired on the
 * thief's and guard's poof-ins, the looted corpse's vanish, and the guard's exit.
 * The client renders the baked `poof` effect strip at `(x, y, z)` and plays the
 * teleport-poof sound proximity-scaled - one message drives both, so the sight
 * and the sound can never disagree. Like {@link StormTelegraphMessage} it is a
 * one-shot moment, never snapshot state; a client may drop it without consequence.
 */
export interface JusticeFxMessage {
  kind: 'JusticeFx';
  /** The one FX this event owns; a field so a future cue can share the message. */
  fx: 'poof';
  /** Absolute map tile the poof plays on. */
  x: number;
  y: number;
  /** The elevation level (0 = ground; the bank roof plate is 20). */
  z: number;
}

/**
 * Server -> client: an ephemeral **blood-splatter** cue (Blood splatter, PRD #372,
 * Slice B) - the {@link JusticeFxMessage} tile-anchored template. The server owns
 * the scatter roll (the injected `bloodRng` seam), so it broadcasts one message and
 * every viewer draws the identical splats: the fight reads as one shared scene. The
 * client renders each `{dx, dy, art}` as a transient ground decal on
 * `(x + dx, y + dy)` at level `z` via {@link ../render/bloodLayer.BloodLayer.splat};
 * an optional `hue` is the `BloodHue` seam (wired but currently unset - splats draw
 * their natural red). Like {@link JusticeFxMessage} it is a one-shot moment, never
 * snapshot/delta state; a client may drop it without consequence, and a mid-splatter
 * late-joiner correctly sees nothing (a 3-second decal is not world state).
 */
export interface BloodFxMessage {
  kind: 'BloodFx';
  /** Absolute map tile of the victim (the scatter box centre). */
  x: number;
  y: number;
  /** The elevation level (0 = ground; the bank roof plate is 20). */
  z: number;
  /** The server-rolled scatter: 1-3 offsets + art picks in the 3x3 box. */
  splats: SplatRoll[];
  /** Optional victim blood hue (the `BloodHue` seam); absent = natural red. */
  hue?: number;
}

/**
 * Server -> client, **broadcast**: wipe every fallen-body sprite this client is holding on
 * the ground (the duel/storm corpse/ghost split, ADR-0140). Corpses are purely client-side
 * figures pooled by {@link GameRenderer}; a storm can down many players at once, so the mass
 * of frozen corpses left behind is a real render cost. This one-shot signal tells clients to
 * clear them all - fired by the server at storm end (belt-and-suspenders over the per-entity
 * resurrection clear) and from the admin dashboard's "Clear all bodies" control. Ephemeral
 * like {@link ActionAnimationMessage}: it carries no state and may be dropped without harm.
 */
export interface ClearCorpsesMessage {
  kind: 'ClearCorpses';
}

/**
 * Server -> client, **owner-only**: this connection's gold balance after a
 * change (PRD #222 / ADR-0119), pushed once at login (the anchor) and on every
 * credit (each rat stomped). For a **reserved** session `gold` is the durable
 * account balance ({@link AccountRecord.gold}, ADR-0115) and `durable` is
 * `true`; for an **anonymous** session it is the non-persisting gold earned
 * *this event* (the in-memory `anonProgress` pattern - it evaporates on
 * disconnect) and `durable` is `false`, which is what lets Slice F's HUD show
 * the "reserve a name to bank gold" nudge without guessing. Like
 * {@link OnlineTimeMessage} it is per-connection account state, never broadcast
 * and never part of the snapshot.
 */
export interface GoldUpdateMessage {
  kind: 'GoldUpdate';
  /** The balance the HUD shows: durable (reserved) or this-event (anonymous). */
  gold: number;
  /** `true` = a persisted account balance; `false` = evaporates on disconnect. */
  durable: boolean;
}

/**
 * Server -> client, **owner-only**: the authoritative total online time for *this
 * connection's* character, in ms (Cosmetic Progression Slice D, PRD #101 / ADR-0044).
 * It is the one number the bottom progression HUD spends - the XP bar, the
 * "M:SS to next unlock" countdown, and the own-time read-out are all derived from
 * it against the shared `UNLOCK_INTERVAL_MS`. The value is whatever the server has
 * tallied for the session: a **reserved** account's persisted `totalOnlineMs`, or
 * an **anonymous** session's live in-memory tally (which evaporates on disconnect).
 *
 * Sent **per-connection** (it is the asking socket's own time, never broadcast) at
 * exactly two moments - on **login** (the first anchor, after `Welcome`) and at each
 * **accrual checkpoint** that advanced the tally (ADR-0040) - so it is *server-
 * anchored*. Between anchors the client **extrapolates** the value forward at
 * wall-clock rate and **snaps** to each fresh push (the same honesty bound the
 * leaderboard uses: the client smooths between server truths, it never invents
 * time). It rides its own tiny message - never the snapshot/delta - because online
 * time is per-connection account state, not shared world state (cf. {@link MoveAckMessage}).
 */
export interface OnlineTimeMessage {
  kind: 'OnlineTime';
  /** This connection's authoritative total online time in ms (the HUD's XP source). */
  totalOnlineMs: number;
}

/**
 * One owned-or-requested cosmetic on the wire: an atomic `{item, hue}` pair
 * (Cosmetic Progression, PRD #101 / ADR-0044). `item` is an `equipment` registry id
 * and `hue` a 1-based UO hue id - the same shape the store's `UnlockedPair` and the
 * sim's `CosmeticRoll` carry, kept self-contained here so the protocol depends on
 * neither the server account layer nor the roll engine. It is the unit both the
 * collection push ({@link CollectionMessage}) and the dressing intent
 * ({@link EquipMessage}) speak in (Cosmetic Progression Slice E, ADR-0050).
 */
export interface WardrobePair {
  /** `equipment` registry id of the cosmetic, e.g. `hair.long`. */
  item: string;
  /** 1-based UO hue id (a distinct hue is a distinct collectible). */
  hue: number;
}

/**
 * Client -> server: dress the player's character from the cosmetics it owns
 * (Cosmetic Progression Slice E, ADR-0050 / ADR-0044). It carries the **whole
 * desired worn outfit** as a flat list of `{item, hue}` pairs, so a single
 * stateless intent expresses both *equip* (a pair present) and *unequip* (a layer's
 * pair omitted; an **empty** list strips to a bare body) with no per-layer command
 * type and no client/server merge race. The server is authoritative: each requested
 * pair is **validated against the account's `unlocked[]`** (you can only wear pairs
 * you own; anonymous sessions validate against their in-memory unlocks), then run
 * through `assembleEquipment` for body-gating, one-item-per-layer and canonical
 * order. The validated stack is persisted to `equippedOutfit` (reserved accounts
 * only - anonymous persists nothing) and broadcast in the next snapshot delta.
 */
export interface EquipMessage {
  kind: 'Equip';
  /** The desired worn cosmetics; an empty list bares the body. Validated server-side. */
  outfit: WardrobePair[];
}

/**
 * Client -> server: set the player's **auto-equip preference** (auto-equip toggle,
 * ADR-0123). The wardrobe's top-of-panel checkbox drives this: `enabled: true` (the
 * default) auto-dresses a freshly-unlocked cosmetic into any empty outfit layer at
 * the ~5-min unlock boundary, exactly as before; `enabled: false` collects the
 * unlock without touching the worn outfit, so the player keeps what they are wearing
 * until they equip the new piece by hand. The preference is authoritative server-side
 * (the auto-equip runs there, in `advanceUnlocks`), so the client cannot suppress it
 * alone - it must send this. Persisted per reserved account (survives reconnect);
 * an anonymous session keeps it in memory only. Sent once when the box is toggled
 * and re-sent after a Welcome to restore a saved choice, like {@link SetSpeechHueMessage}.
 */
export interface SetAutoEquipMessage {
  kind: 'SetAutoEquip';
  /** `true` = auto-equip new unlocks into empty layers (default); `false` = collect only. */
  enabled: boolean;
}

export interface UnlockWardrobeFavoriteMessage {
  kind: 'UnlockWardrobeFavorite';
}

export interface SaveWardrobeFavoriteMessage {
  kind: 'SaveWardrobeFavorite';
  slot: number;
  outfit: WardrobePair[];
}

export interface WardrobeFavoritesStateMessage {
  kind: 'WardrobeFavoritesState';
  reserved: boolean;
  favorites: (WardrobePair[] | null)[];
}

/**
 * Client -> server: an admin toggles **no-clip** (walk through blocked tiles/statics),
 * ADR-0183. Server-authority: honoured **only** when the sending connection's
 * passcode-proven `accountKey` is a configured admin (`isAdmin`); every other sender
 * (non-admin reserved, anonymous, pre-login) is a logged no-op. The session flag is
 * default-off, session-scoped, and never persisted - it resets on reconnect. `enabled`
 * is coerced to a strict boolean in the handler (the wire is untrusted).
 */
export interface AdminSetNoClipMessage {
  kind: 'AdminSetNoClip';
  enabled: boolean;
}

/**
 * Client -> server: an admin **teleports** their own character to a tile, ADR-0183.
 * Same admin gate as {@link AdminSetNoClipMessage}. `x`/`y` are validated as integers
 * that are in-bounds on the CURRENT map in the handler (any in-bounds tile is allowed -
 * walls/sealed/scenery included - that is the point of admin teleport); `z` resolves to
 * the ground plane (0). The move is applied off the world queue and forced onto the
 * predicting client via a server-initiated (seq-0) `MoveReject`, exactly like a
 * teleporter landing; other clients see it in the next snapshot delta.
 */
export interface AdminTeleportMessage {
  kind: 'AdminTeleport';
  x: number;
  y: number;
}

/** The character-size actions available from the in-world admin panel. */
export type AdminSize = 'grow' | 'shrink' | 'normal';

/**
 * Client -> server: an admin grows or shrinks their own character for one hour, or
 * returns it to normal size immediately. The server re-authorizes the sender and owns
 * both the replicated scale and expiry.
 */
export interface AdminSetSizeMessage {
  kind: 'AdminSetSize';
  size: AdminSize;
}

/**
 * Client -> server: equip the admin-only oversized bascinet on the sender's own
 * live character. The server re-authorizes the socket, removes existing hair,
 * beard, and headgear, and adds the system headgear without changing the saved
 * wardrobe.
 */
export interface AdminEquipBascinetMessage {
  kind: 'AdminEquipBascinet';
}

/**
 * Client -> server: an admin puts themselves on a mount, or takes themselves off it
 * (ADR-0442). `body` is the mount's UO body id; omitting it always dismounts, and
 * sending the body already ridden dismounts too, so a single admin-panel button
 * toggles.
 *
 * The server re-authorizes the sender and validates `body` against the registered
 * mount bodies, so this can never be used to draw an arbitrary creature under a
 * player. Purely cosmetic: it changes the replicated layer stack and nothing about
 * movement, collision, or reach.
 */
export interface AdminSetMountMessage {
  kind: 'AdminSetMount';
  body?: number;
}

/**
 * Server -> client, **owner-only**: the authoritative current **auto-equip
 * preference** for this connection's character (auto-equip toggle, ADR-0123). Sent
 * once at **login** (after `Welcome`, alongside the collection push) so the wardrobe
 * checkbox opens reflecting the account's persisted choice rather than a bare
 * default - a returning owner who unchecked it last session sees it unchecked. It is
 * per-connection account state, not shared world state, so it rides its own tiny
 * message off the snapshot stream (the {@link OnlineTimeMessage}/{@link CollectionMessage}
 * pattern), never broadcast to peers.
 */
export interface AutoEquipStateMessage {
  kind: 'AutoEquipState';
  /** This connection's current auto-equip preference (see {@link SetAutoEquipMessage}). */
  enabled: boolean;
}

/**
 * Server -> client: the character's **earned cosmetic collection** - every
 * `{item, hue}` pair it owns (Cosmetic Progression Slice E, ADR-0050). The wardrobe
 * panel needs the collection to render its layer -> item -> hue grouping, and the
 * collection lives only server-side (in the account record, or in the anonymous
 * session's in-memory progress), so the server pushes it here: once right after the
 * login's retroactive grant, and again whenever a fresh unlock grows it. It is the
 * collection, **not** the worn outfit - what a player *may* wear; the outfit they
 * *are* wearing rides the ordinary entity snapshot (`renderable.equipment`). Sent
 * for both reserved and anonymous sessions, so an anonymous wardrobe works in memory
 * exactly like a reserved one (PRD #101's "same loop in memory").
 */
export interface CollectionMessage {
  kind: 'Collection';
  /** Every owned `{item, hue}` pair, in unlock order. */
  unlocked: WardrobePair[];
}

/**
 * The four rarities a card exists in, as a self-contained wire literal. Declared
 * here (not imported from `sim/cardContent`) so `protocol.ts` stays free of a sim
 * dependency exactly as {@link WardrobePair} stays free of the account layer; it is
 * structurally identical to the sim `CardRarity`.
 */
export type WireCardRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

/** One concrete TCG card shared through chat. */
export interface WireCardLink {
  cardId: string;
  rarity: WireCardRarity;
}

/** One collection cell on the wire: copies of a concrete `(cardId, rarity)` card owned. */
export interface WireCardEntry {
  cardId: string;
  rarity: WireCardRarity;
  /** Copies owned (>= 1). */
  count: number;
}

/** One deck slot on the wire: a card name at a chosen rarity (mirrors the sim `DeckCardRef`). */
export interface WireDeckCard {
  cardId: string;
  rarity: WireCardRarity;
}

/** One saved, named deck on the wire (mirrors the sim `NamedDeck`). */
export interface WireNamedDeck {
  name: string;
  cards: WireDeckCard[];
  /** Preferred deck for preselecting card-game flows. At most one may be true. */
  default?: boolean;
}

/** One server-authored Card Master Carl challenge in the current daily slate. */
export interface WireCardDailyChallenge {
  id: string;
  opponentRecipeId: StarterRecipeId;
  opponentName: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  rewardGold: number;
  completed: boolean;
}

/**
 * Server -> client, **owner-only**: the character's whole **Card Binder state**
 * (trading card game, PRD #493 Slice 3) - the owned collection, the saved named
 * decks, and the unopened pack count. Cards are reserved-name-only exactly like
 * gold, so `reserved` tells the Binder whether to render the collection or the
 * "cards require a reserved name" prompt (PRD story 31); an anonymous session gets
 * `reserved: false` with empty lists. Pushed in response to the client's {@link
 * OpenBinderMessage} (which also triggers the one-time starter grant) and again
 * after a {@link SaveCardDecksMessage} so the client always mirrors the
 * authoritative record. It is per-connection account state, not shared world state,
 * so it rides its own tiny message off the snapshot stream (the {@link
 * CollectionMessage} pattern), never broadcast to peers.
 */
export interface CardStateMessage {
  kind: 'CardState';
  /** Whether this connection owns a reserved name (cards persist only then). */
  reserved: boolean;
  /** Every owned card cell, `(cardId, rarity, count)`. Empty for an anonymous session. */
  collection: WireCardEntry[];
  /** The saved named decks (up to three). Empty for an anonymous session. */
  decks: WireNamedDeck[];
  /** Unopened card packs held (the Packs tab surfaces this; opening is Slice 4). */
  packs: number;
  /** Chosen recipe; legacy Starter owners report Britannian Basics. */
  starterRecipe?: StarterRecipeId | null;
  /** True only while this reserved account may make its permanent one-time choice. */
  starterEligible?: boolean;
  /** Three full-health Carl wins available during the current UTC day. */
  dailyChallenges: WireCardDailyChallenge[];
  /** Gold already earned from today's completed challenge wins. */
  dailyChallengeEarnedGold: number;
  /** Hard server-side ceiling for challenge gold during this UTC day. */
  dailyChallengeLimitGold: number;
  /** Unix milliseconds for the next UTC challenge reset. */
  dailyChallengeResetsAt: number;
}

/** Client -> server: explicitly confirm the reserved account's one permanent starter. */
export interface ChooseStarterRecipeMessage {
  kind: 'ChooseStarterRecipe';
  recipeId: StarterRecipeId;
}

/**
 * Server -> client, **owner-only**: the five cards a just-opened pack revealed
 * (trading card game, PRD #493 Slice 4), in the order the flip-reveal modal turns
 * them. Pushed only to the opener in reply to a validated `OpenCardPack` command,
 * right before the authoritative {@link CardStateMessage} that reflects the cards
 * banked and the pack count decremented. The rarities drive the modal's per-card
 * rarity burst and the legendary gold-flash + sound sting; the collection is
 * already updated by the accompanying `CardState`, so the modal is pure
 * presentation. Ephemeral - its own tiny message off the snapshot stream, never
 * broadcast to peers (the {@link CardStateMessage} pattern).
 */
export interface PackOpenedMessage {
  kind: 'PackOpened';
  /** The five revealed cards, `(cardId, rarity)`, in flip order. */
  cards: WireDeckCard[];
  /** Gold rebated for retained copies beyond the playable two-copy limit. */
  duplicateGold: number;
}

/**
 * Client -> server: the player **opened the Card Binder** (trading card game, PRD
 * #493 Slice 3). On the first open of a reserved account the server performs the
 * one-time {@link starterGranted starter-deck grant} (idempotent - safe to send on
 * every open), then replies with a {@link CardStateMessage}. An anonymous session
 * grants nothing and gets a `reserved: false` state so the Binder shows the
 * reserve-a-name prompt. Carries no payload - the authority is the connection's
 * `accountKey`, never the wire.
 */
export interface OpenBinderMessage {
  kind: 'OpenBinder';
}

/** Request the current account's authoritative Soulstone roster (PRD #837). */
export interface RequestSoulstonesMessage {
  kind: 'RequestSoulstones';
}

/**
 * Attune one empty Soulstone slot to a declared class (PRD #837).
 *
 * The server mints the durable Soulstone id, so the message carries only the
 * class choice; a duplicate class across stones is legal and the only refusal
 * beyond class validity is a full set of slots.
 */
export interface AttuneSoulstoneMessage {
  kind: 'AttuneSoulstone';
  classId: string;
}

/** Equip one account-owned stash item onto an owned Soulstone's Hero. */
export interface EquipHeroItemMessage {
  kind: 'EquipHeroItem';
  soulstoneId: string;
  itemId: string;
}

/**
 * Take one slot's item off an owned Soulstone's Hero (PRD #795).
 *
 * The slot is the intent rather than the item, so a retry of a message the
 * client already sent empties a slot that is already empty instead of
 * unequipping whatever landed there since.
 */
export interface UnequipHeroItemMessage {
  kind: 'UnequipHeroItem';
  soulstoneId: string;
  slot: string;
}

/**
 * Spend attribute points on an owned Soulstone's Hero (PRD #792).
 *
 * Carries the **whole desired allocation** rather than a delta, the
 * {@link EquipMessage} whole-outfit shape: a dropped or duplicated message
 * cannot leave a Hero half-allocated. The server validates it against the
 * Hero's level; the client never computes what it is allowed to spend.
 */
export interface AllocateHeroStatsMessage {
  kind: 'AllocateHeroStats';
  soulstoneId: string;
  stats: HeroStats;
}

/** Refund every allocated point on an owned Soulstone's Hero for the declared gold price. */
export interface RespecHeroMessage {
  kind: 'RespecHero';
  soulstoneId: string;
}

/**
 * Take up one owned Soulstone inside the Bone Knight Wall (PRD #837 slice 3):
 * select which knight the session carries, so the hub's far exit knows whose
 * campaign it resolves. Meaningful only while the session stands in the hub;
 * anywhere else the server answers the roster with `hub-required`.
 */
export interface TakeUpSoulstoneMessage {
  kind: 'TakeUpSoulstone';
  soulstoneId: string;
  /**
   * The difficulty tier to take the stone up at (PRD #837 slice 7): which
   * tier the hub's far exit walks. Absent is Normal. A bare string on the
   * wire, narrowed server-side against the tier ids and the stone's own
   * derived unlocks; a locked or unknown tier answers `tier-locked`.
   */
  tier?: string;
}

/**
 * Wipe one owned Soulstone (PRD #837 slice 3): destroy the stone, its Hero,
 * its allocation, and its campaign progress, freeing the slot to attune anew.
 * Destructive and irreversible, so the dialog flow requires an explicit
 * confirmation step before this is ever sent. Worn items return to the
 * account stash (they were stash rows all along); a stone whose Hero is on a
 * live run refuses with `soulstone-in-use`.
 */
export interface WipeSoulstoneMessage {
  kind: 'WipeSoulstone';
  soulstoneId: string;
}

export type SoulstoneRosterError =
  | 'account-required'
  | 'invalid-class'
  | 'unavailable-class'
  | 'slots-full'
  | 'soulstone-required'
  | 'invalid-hero'
  | 'item-required'
  | 'class-invalid'
  | 'allocation-invalid'
  | 'gold-required'
  | 'hub-required'
  | 'soulstone-in-use'
  | 'tier-locked';

/** Owner-only durable Soulstone roster, returned on request and after every mutation. */
export interface SoulstoneRosterMessage {
  kind: 'SoulstoneRoster';
  reserved: boolean;
  soulstones: Soulstone[];
  stash: ArpgItem[];
  /** The account's durable gold, so the panel can price a respec. */
  gold: number;
  /**
   * The Soulstone this session has taken up in the Bone Knight Wall (PRD #837
   * slice 3), absent outside the hub or before a take-up. The dialog renders
   * the selection from this authoritative echo, never from its own click.
   */
  selectedSoulstoneId?: string;
  /**
   * The tier the taken-up stone was taken up at (PRD #837 slice 7), beside
   * `selectedSoulstoneId` and under the same rule: the dialog renders the
   * selection from this authoritative echo, never from its own click.
   */
  selectedTier?: CampaignTierId;
  /**
   * The town moongate's campaign destinations per Soulstone (PRD #837
   * slices 4 and 7): every declared dungeon node whose waypoint that stone
   * has used and every camp it has reached, at every tier its ledgers have
   * unlocked, tier-stamped, in tier then graph order, keyed by the stone's
   * id. A destination resolves to a campaign run of that node
   * (`StartDungeonRun` with `campaignNodeId` and `campaignTier`).
   * Additive-optional, so older clients ignore it.
   */
  campaignDestinations?: Record<string, CampaignMoongateDestination[]>;
  /**
   * The tiers each stone's ledgers have unlocked (PRD #837 slice 7), in
   * unlock order, keyed by the stone's id. Data for the dialog's tier choice;
   * the server re-derives on every use and trusts none of it back.
   */
  unlockedTiers?: Record<string, CampaignTierId[]>;
  error?: SoulstoneRosterError;
}

/**
 * Client -> server: **save the player's named decks** (trading card game, PRD #493
 * Slice 3 / story 10). Carries the whole desired set of up to three named decks
 * (the stateless whole-set intent, like {@link EquipMessage}'s whole-outfit list).
 * The server is authoritative: it accepts the save only for a reserved account, only
 * up to {@link MAX_NAMED_DECKS} decks, and only when every deck is a legal 20-card
 * deck the account actually owns (`validateDeck` + ownership against the collection,
 * the way {@link EquipMessage} validates worn cosmetics against the owned set), then
 * persists and echoes the authoritative {@link CardStateMessage} back so a rejected
 * or clamped save reverts the client. The deck-builder validates the same rules
 * client-side, so a well-behaved client never sends an illegal set.
 */
export interface SaveCardDecksMessage {
  kind: 'SaveCardDecks';
  decks: WireNamedDeck[];
}

/**
 * Server -> client, **owner-only**: open or dismiss the duel accept/decline
 * window on the challenged player (Player duels, PRD #252 / ADR-0130). Sent only
 * to the target's socket - a challenge is a private call-out, not broadcast; the
 * whole room hears the spoken "I wish to duel" line as ordinary {@link SpeechMessage}
 * speech, but the accept/decline prompt is the target's alone.
 *
 * `open: true` shows the window naming the challenger and the stake ("NAME has
 * challenged you to a duel to the death for X gp. Do you accept?"); `open: false`
 * dismisses it (the challenge was withdrawn, expired, resolved, or the challenger
 * disconnected), with the other fields unused. Ephemeral like {@link SpeechMessage}
 * / {@link RatSwarmStatusMessage}: a challenge is an event, never world state, so
 * it rides its own tiny message and is never part of the snapshot/delta - a late
 * joiner correctly sees nothing.
 */
export interface DuelPromptMessage {
  kind: 'DuelPrompt';
  /** `true` shows the accept/decline window; `false` dismisses it. */
  open: boolean;
  /** The challenger's entity id (routes the prompt / labels the challenger); `0` when closing. */
  challenger: EntityId;
  /** The challenger's display name, shown in the prompt; empty when closing. */
  challengerName: string;
  /** The staked gold shown in the prompt (`0` = an honor duel); `0` when closing. */
  wager: number;
}

/**
 * Server -> client, **owner-only**: name this player's live duel **opponent** so
 * the client can drive the "Press F to auto-fire your bow!" prompt (Fire your bow!,
 * ADR-0255). Sent to each duelist when combat opens (beside the private "attacking
 * you!" line in `openDuelCombat`); the client stores the id and computes the tile
 * distance to it each frame to decide when to show the prompt (the opponent kited
 * out of punch range but still in bow range) and where a fired shot lands.
 *
 * The client clears its stored opponent when the local player's replicated
 * `renderable.combatStance` drops (every duel end - win / loss / draw / forfeit /
 * storm-cancel - drops the stance), so no explicit "combat closed" message is
 * needed. Ephemeral like {@link DuelPromptMessage}: a duel is an event off the
 * snapshot stream, never world state, so a late joiner correctly sees nothing.
 */
export interface DuelCombatMessage {
  kind: 'DuelCombat';
  /** The local player's duel opponent entity id (the fire-bow target). */
  opponent: EntityId;
}

/** Owner-only accept or decline prompt for a direct Sphere Duel challenge. */
export interface SpherePromptMessage {
  kind: 'SpherePrompt';
  open: boolean;
  challenger: EntityId;
  challengerName: string;
  wager: number;
}

/** Owner-only knowledge for one participant in a live private Sphere Duel. */
export interface SphereStateMessage extends SpherePlayerView {
  kind: 'SphereState';
  /** Fresh for every round/rematch; scopes speculative command correlation. */
  matchId?: number;
  /** Owner-only recovery anchor for the next client command sequence. */
  nextCommandSequence?: number;
  youId: EntityId;
  opponentId: EntityId;
  opponentName: string;
  record: { wins: number; losses: number; draws: number };
  reconnectTicks?: number;
  /** Result from this owner's perspective, present during the result overlay. */
  result?: 'win' | 'loss' | 'draw' | 'cancelled';
  /** Result-screen rematch intent from this owner's perspective. */
  rematch?: { you: boolean; opponent: boolean };
  wager: number;
}

/** Observable spell, potion, countdown, death, and result presentation cue. */
export interface SphereEventMessage {
  kind: 'SphereEvent';
  effect: SphereEffect;
  /** Present only for the originating owner when this effect answers a command. */
  command?: { matchId: number; sequence: number };
}

/** Owner-only authoritative acceptance/rejection of a speculative Sphere command. */
export interface SphereCommandResultMessage {
  kind: 'SphereCommandResult';
  matchId: number;
  sequence: number;
  accepted: boolean;
  reason?: SphereFizzleReason;
}

/**
 * Server -> client, **owner-only**: open or dismiss the **dice-wager** accept/decline
 * window on the challenged player (Dice Wager, ADR-0154). The dice-wager twin of
 * {@link DuelPromptMessage} - identical shape, distinct `kind` so the client shows dice
 * copy ("challenged you to a dice wager") rather than the duel's "to the death". Sent
 * only to the target's socket - the challenge itself is heralded to the whole bank as an
 * ordinary broadcast herald line, but the accept/decline prompt is the target's alone.
 *
 * `open: true` shows the window naming the challenger and the stake; `open: false`
 * dismisses it (the challenge was declined, expired, resolved, or the challenger
 * disconnected), with the other fields unused. Ephemeral like {@link DuelPromptMessage}:
 * a challenge is an event, never world state, so it rides its own tiny message and is
 * never part of the snapshot/delta - a late joiner correctly sees nothing.
 */
export interface DiceWagerPromptMessage {
  kind: 'DiceWagerPrompt';
  /** `true` shows the accept/decline window; `false` dismisses it. */
  open: boolean;
  /** The challenger's entity id (routes the prompt / labels the challenger); `0` when closing. */
  challenger: EntityId;
  /** The challenger's display name, shown in the prompt; empty when closing. */
  challengerName: string;
  /** The staked gold shown in the prompt (`0` = an honor roll); `0` when closing. */
  wager: number;
}

/**
 * Server -> client, **owner-only**: open or dismiss the **card-duel** accept/decline
 * window on the challenged player (trading card game, PRD #493 Slice 5, ADR-0286). The
 * card-game twin of {@link DuelPromptMessage} / {@link DiceWagerPromptMessage} - identical
 * shape, distinct `kind` so the client shows card-duel copy ("challenged you to a card
 * duel"). Sent only to the target's socket. `open: false` dismisses (declined, expired,
 * resolved, or the challenger disconnected). Ephemeral like {@link DuelPromptMessage}: a
 * challenge is an event, never world state, so it rides its own tiny message.
 */
export interface CardDuelPromptMessage {
  kind: 'CardDuelPrompt';
  /** `true` shows the accept/decline window; `false` dismisses it. */
  open: boolean;
  /** The challenger's entity id (routes the prompt / labels the challenger); `0` when closing. */
  challenger: EntityId;
  /** The challenger's display name, shown in the prompt; empty when closing. */
  challengerName: string;
  /** The staked gold shown in the prompt (`0` = an honor card duel); `0` when closing. */
  wager: number;
}

/** Public, privacy-safe projection of one player advertising for a card duel. */
export interface WireCardDuelListing {
  /** Opaque identity for accepting these exact terms. */
  id: string;
  playerName: string;
  /** Per-player stake; zero means an honor duel. */
  wager: number;
  /** Server epoch milliseconds, used by clients to render the elapsed wait. */
  createdAt: number;
}

/** Server -> logged-in client: current public card-duel board. */
export interface CardDuelListingsMessage {
  kind: 'CardDuelListings';
  listings: WireCardDuelListing[];
  /** Present only for the viewer who owns a listing. */
  yourListingId?: string;
}

/** Owner-only match-found event sent after a listing is successfully claimed. */
export interface CardDuelListingAcceptedMessage {
  kind: 'CardDuelListingAccepted';
  listingId: string;
  opponentName: string;
  wager: number;
}

/**
 * One card as it appears in a {@link CardDuelStateMessage} - a self-contained wire twin
 * of the sim `CardInstance` (no sim import, the {@link WireCardEntry} discipline). Carries
 * only what the battle gump renders: identity, cost, and combat runtime.
 */
export interface WireBattleCard {
  /** Stable per-match instance id (names the card in a {@link CardDuelActionCommand}). */
  instanceId: number;
  cardId: string;
  name: string;
  rarity: WireCardRarity;
  kind: 'monster' | 'spell' | 'item';
  cost: number;
  /** Current attack (0 for spells and items). */
  attack: number;
  /** Current health (0 for spells and items). */
  health: number;
  /** Full health (0 for spells and items). */
  maxHealth: number;
  /** Keyword ids present (`'taunt'` / `'charge'`). */
  keywords: string[];
  /** Whether this minion may still attack this turn (board cards only). */
  canAttack: boolean;
  /**
   * Whether playing this card requires the player to pick a target (a hero or minion) -
   * true only for targeted spell and item effects. The battle gump enters
   * target-select on such a card; the engine
   * still rules the target's legality authoritatively.
   */
  needsTarget?: boolean;
  /** Server-authored legal candidates for this viewer at this state revision. */
  legalTargets?: WireCardTargetCandidate[];
}

/** Advisory target metadata; every submitted id is revalidated against live state. */
export type WireCardTargetCandidate =
  | { kind: 'hero'; seat: 0 | 1 }
  | { kind: 'minion'; instanceId: number }
  | {
      kind: 'graveyard';
      instanceId: number;
      cardId: string;
      name: string;
      rarity: WireCardRarity;
      attack: number;
      health: number;
      keywords: string[];
      slayerGroup?: string;
      /** Zero-based stable death order; larger means more recent. */
      deathOrder: number;
    };

/**
 * One hero's public battle side in a {@link CardDuelStateMessage}: hero health, mana, the
 * face-up board, and the *counts* of the hidden zones (a foe's hand is never sent
 * card-by-card - only its size).
 */
export interface WireBattleSide {
  heroHealth: number;
  heroMaxHealth: number;
  /** Remaining damage absorption on Reactive Armor, when active. */
  reactiveArmor?: number;
  /** Whether Magic Reflection is waiting for an enemy targeted damage spell. */
  magicReflection?: boolean;
  /** Whether a symmetric Blood Oath damage link is active for both heroes. */
  bloodOath?: boolean;
  mana: number;
  maxMana: number;
  /** Cards in hand (a count only - a foe's hand is hidden, and your own is in `hand`). */
  handCount: number;
  /** Cards left in the draw pile. */
  deckCount: number;
  /** The face-up board minions. */
  board: WireBattleCard[];
}

/** One ordered line in the server-authored card-duel transcript. */
export interface WireCardDuelLogEntry {
  /** Monotonic match-local identity, used to announce repeated identical events exactly once. */
  sequence: number;
  /** The engine turn in which the event occurred. */
  turn: number;
  /** Ordinary shared history or a private rejected-action message. */
  tone: 'event' | 'error';
  text: string;
}

/** A stable target named by a server-authored card-battle presentation beat. */
export type WireCardBattleTarget =
  | { kind: 'hero'; seat: 0 | 1 }
  | { kind: 'minion'; instanceId: number };

/**
 * One accepted attack for the battle scene to present in order.
 * This is semantic history, not animation instructions: the client chooses the available
 * UO clips and graceful fallbacks for the attacking card body.
 */
export interface WireCardBattleAttackBeat {
  /** Monotonic match-local identity used by the client presentation queue. */
  sequence: number;
  kind: 'attack';
  /** Absolute engine seat of the attacker. */
  seat: 0 | 1;
  attackerId: number;
  attackerCardId: string;
  /** Rarity fixes the attack sprite hue to the same variant shown on the card. */
  attackerRarity: WireCardRarity;
  target: WireCardBattleTarget;
}

/**
 * One accepted card play. The following effect beats carry its ordered outcome;
 * keeping the play separate lets the client show the caster before the result.
 */
export interface WireCardBattlePlayBeat {
  sequence: number;
  kind: 'play';
  seat: 0 | 1;
  instanceId: number;
  cardId: string;
  rarity: WireCardRarity;
  cardKind: 'monster' | 'spell' | 'item';
  /** Graveyard choices stay owner-private and are represented by later summon beats. */
  target?: WireCardBattleTarget;
}

/** The presentation-relevant subset of the pure engine's ordered effects. */
export type WireCardBattleEffectKind =
  | 'summon'
  | 'return-to-hand'
  | 'damage'
  | 'heal'
  | 'buff'
  | 'status'
  | 'delayed-damage'
  | 'death'
  | 'ability'
  | 'fizzle'
  | 'ward'
  | 'draw'
  | 'burn'
  | 'fatigue'
  | 'turn-start'
  | 'settle';

/**
 * One semantic engine effect. Optional fields are populated only when meaningful
 * for `effect`; this remains presentation data, never authoritative card state.
 */
export interface WireCardBattleEffectBeat {
  sequence: number;
  kind: 'effect';
  effect: WireCardBattleEffectKind;
  seat?: 0 | 1;
  sourceSeat?: 0 | 1;
  sourceInstanceId?: number;
  sourceCardId?: string;
  sourceRarity?: WireCardRarity;
  instanceId?: number;
  cardId?: string;
  rarity?: WireCardRarity;
  target?: WireCardBattleTarget;
  amount?: number;
  attack?: number;
  health?: number;
  action?: 'apply' | 'clear' | 'arm' | 'detonate' | 'fizzle' | 'gain' | 'trigger';
  status?: 'paralyze';
  ward?: 'reactive-armor' | 'magic-reflection';
  text?: string;
  turnNumber?: number;
  winner?: 0 | 1 | null;
  reason?: string;
  /**
   * True for an opponent draw/burn. In that projection `cardId` and `rarity`
   * are absent, so hidden hand identity never enters the opposing client.
   */
  hiddenCard?: boolean;
}

export type WireCardBattleBeat =
  | WireCardBattleAttackBeat
  | WireCardBattlePlayBeat
  | WireCardBattleEffectBeat;

/**
 * Server -> client, **owner-only, per-seat directed**: the whole live **card-duel battle
 * state** as one player sees it (trading card game, PRD #493 Slice 5, ADR-0286). The
 * server builds a *different* message for each duelist (your own `hand` is full; the foe's
 * hand is only a `handCount` in `foe`), so a card is never leaked to the wrong client.
 * The battle gump renders directly from this - it holds no game logic (ADR-0268), it just
 * mirrors the authoritative state and sends {@link CardDuelActionCommand} intents back.
 *
 * `open: false` closes the gump (the match ended or was aborted); the accompanying
 * `phase`/`outcome` drive the end-of-match banner just before it closes. Ephemeral like
 * {@link DuelPromptMessage} - never part of the snapshot/delta.
 */
export interface CardDuelStateMessage {
  kind: 'CardDuelState';
  /** Monotonic server-local match identity which scopes presentation beat sequences. */
  matchId: number;
  /** `true` shows/updates the battle gump; `false` closes it. */
  open: boolean;
  /** The viewing player's live-world entity, used to render their current battle portrait. */
  youId: EntityId;
  /** The opposing player or card-master entity, used to render their current battle portrait. */
  opponentId: EntityId;
  /** The opponent's display name, shown in the gump header. */
  opponentName: string;
  /** The staked gold (`0` = an honor card duel), shown in the header. */
  wager: number;
  /** This client's seat (0 = challenger, 1 = target); `foe` is the other seat. */
  yourSeat: 0 | 1;
  /** Whether it is this client's turn to act. */
  yourTurn: boolean;
  /** The turn number (1-based). */
  turnNumber: number;
  /** Whole seconds left on the mulligan window or acting player's turn clock. */
  secondsRemaining: number;
  /** Whether this viewer has locked their opening-hand choice. */
  mulliganSubmitted: boolean;
  /** Whether this viewer has no legal gameplay action and the server is about to skip their turn. */
  autoSkipPending: boolean;
  /** This client's full hand (face-up - it is theirs). */
  hand: WireBattleCard[];
  /** This client's own public side (hero, mana, board, zone counts). */
  you: WireBattleSide;
  /** The opponent's public side (their hand is a count only). */
  foe: WireBattleSide;
  /** Ordered server-authored history, including private illegal-move feedback for this viewer. */
  log: WireCardDuelLogEntry[];
  /** Ordered shared semantic actions consumed by the animated battle scene. */
  beats: WireCardBattleBeat[];
  /** Whether players are choosing opening hands, playing, or finished. */
  phase: 'mulligan' | 'playing' | 'ended';
  /** The ended-match outcome from this client's point of view (undefined while playing). */
  outcome?: 'win' | 'loss' | 'draw';
}

/**
 * Server -> client: one **floating combat number** over a duelist (Player duels,
 * PRD #252, ADR-0131) - the net-new client feedback layer for a duel swing. A
 * landed hit sends a `damage` float (the number, e.g. `"7"`) over the **victim**,
 * which the client rises-and-fades in red over the head (see `combatText.ts`). A
 * missed swing still sends a `miss` message over the **attacker**, but the client
 * floats **nothing** for it - the message survives only to carry the shared whiff
 * (a miss plays no flinch clip, so its sound rides this signal; the hit's grunt
 * rides the victim's flinch clip instead). The `text` on a `miss` is unused.
 *
 * Ephemeral like {@link SpeechMessage}/{@link ActionAnimationMessage}: a swing is
 * an event, never world state, so it rides its own tiny message and is never part
 * of the snapshot/delta - a late joiner correctly sees nothing. Broadcast to every
 * connection so onlookers read the brawl too (the volume is proximity-scaled
 * client-side).
 */
export interface CombatTextMessage {
  kind: 'CombatText';
  /** The struck victim on a hit (the float rises over them); the attacker on a miss (nothing floats - it only carries the whiff). */
  entityId: EntityId;
  /** The damage number to float on a hit; unused on a miss. */
  text: string;
  /** `damage` = a landed hit's number; `miss` = a whiffed swing. */
  outcome: 'damage' | 'miss';
}

/** Owner-only authoritative combat state for one generated dungeon run. */
export interface DungeonRunStatusMessage extends DungeonCombatView {
  kind: 'DungeonRunStatus';
  active: boolean;
}

/**
 * Server -> client: one announced area, sent the moment a telegraphed cast
 * begins and drawn until it resolves (PRD #793).
 *
 * The kernel's counterplay law says a hostile area must be announced before
 * anything happens in it, and an announcement the player cannot see the shape of
 * is not an announcement. So this carries the frozen geometry itself - the
 * declared shape plus the anchor it was frozen against - rather than a marker
 * tile, and the client derives the exact damage footprint from it. One message
 * per footprint, so a pattern that announces several sends several.
 *
 * Ephemeral like {@link StormTelegraphMessage} and {@link ActionAnimationMessage}:
 * a one-shot moment, never snapshot state, and a client that drops it misses a
 * warning rather than desyncing.
 */
export interface DungeonTelegraphMessage {
  kind: 'DungeonTelegraph';
  /** Who is committing to it, so a client can tie the area to the caster's pose. */
  caster: EntityId;
  /** The declared delivery geometry, exactly as resolution will read it. */
  shape: AbilityShape;
  /** Where the aim froze: the caster's own position at cast start. */
  origin: { x: number; y: number };
  /** The aimed point, for a shape placed at one rather than measured from the caster. */
  point?: { x: number; y: number };
  facing: Direction;
  /** Server ticks (50ms) of warning left, so the drawing ends as the damage lands. */
  ticksUntil: number;
}

/** Reconciles one optimistically presented cursor cast against server authority. */
export type DungeonCastResultRejection =
  | DungeonCastRejection
  | 'malformed';

export interface DungeonCastResultMessage {
  kind: 'DungeonCastResult';
  seq: number;
  accepted: boolean;
  reason?: DungeonCastResultRejection;
  mana: number;
  /**
   * Every loadout ability's remaining cooldown, keyed by ability id. One map
   * rather than a field per ability, so a new ability needs no protocol change.
   */
  cooldowns: Record<string, number>;
}

/**
 * One drafted Arena fire modifier on the wire: its id and level (fire-mage
 * rework, ADR-0179). A slim projection of the sim's build so the run HUD can
 * render it at a glance.
 */
export interface ArenaModifierEntry {
  id: ArenaModifierId;
  level: number;
  /** Whether this modifier's keystone transcendence is owned (PRD #378). */
  keystone?: boolean;
}

/**
 * Server -> client, **owner-only**: one skill volley's full FX cascade (fire-mage
 * rework, ADR-0179) - the projectile flights, chain hops, explosions, and flame
 * strikes of a single `skill-fire` beat, in the sim's depth-first resolution
 * order, plus the per-hit damage numbers (skill hits no longer send
 * {@link CombatTextMessage} - the floats render from these events). The client
 * staggers the animation by each event's `gen`/order; every point of damage is
 * already applied sim-side, so a dropped message only misses the show.
 * Ephemeral like {@link ActionAnimationMessage}: never snapshot state.
 */
export interface ArenaFxMessage {
  kind: 'ArenaFx';
  /** The volleying runner. */
  source: EntityId;
  /** The cascade, in depth-first resolution order (the sim's `ArenaFxEvent[]` verbatim). */
  events: ArenaFxEvent[];
  /**
   * The authoritative cast facing the server turned the caster toward (ADR-0192),
   * mirrored by the owner via {@link PredictedWalker.faceCast}. Present on a
   * `skill-fire` volley; absent on a `ground-fx` push (which never turns the
   * caster). Load-bearing for a straight-shot volley, whose traveling-projectile
   * FX inversion (Slice D) puts no projectile event on the wire for
   * `volleyFacing` to read - without this the walker's facing would diverge from
   * the server's and corrupt the walk gate. `null` = no turn (same-tile target).
   */
  castFacing?: Direction | null;
}

/**
 * Server -> client, **owner-only**: the live state of this connection's Arena run
 * (Arena Slice D, PRD #334 / ADR-0176) - the {@link StormStatusMessage} pattern, but
 * private: a run is a solo instance, so its status goes to the owning socket alone.
 * Pushed on entry, whenever a beat/bite changed something, on a cadence between
 * changes, and one final `active: false` at run end. Ephemeral like every event
 * board: run state is server memory beside the run's world, never snapshot state.
 * Slice E's run HUD (timer, level + XP bar, build, kills, health) renders from this.
 */
export interface ArenaRunStatusMessage {
  kind: 'ArenaRunStatus';
  /** Whether the run is live (running or draft-paused). `false` on the final push. */
  active: boolean;
  challengeId?: string;
  challengeVersion?: number;
  challengeName?: string;
  /** The runner's health pool (the HUD health bar). */
  hp: number;
  maxHp: number;
  /** The runner's in-run level and XP toward the next (the HUD XP bar). */
  level: number;
  xp: number;
  xpToNext: number;
  /** Waves spawned so far (the opening wave is 1). */
  wave: number;
  /** Horde enemies currently alive. */
  enemyCount: number;
  /** Enemies killed this run. */
  kills: number;
  /** Server ticks (50ms) elapsed this run - the arena is endless (ADR-0210), so the HUD
   * counts this **up** (time survived) rather than down; the client may extrapolate. */
  elapsedTicks: number;
  /** The base skill's current projectile look (flips to `fireball` with Greater Projectile). */
  projectile: ArenaProjectileKind;
  /** The drafted fire modifiers, in the fixed {@link ArenaModifierId} table order. */
  modifiers: ArenaModifierEntry[];
}

/**
 * Server -> client, **owner-only**: the pending Arena level-up draft (Arena Slice D):
 * the level it rewards and the seeded options to pick from. The run is paused while
 * this is outstanding; the client answers with a `PickArenaOption` command carrying
 * the chosen index, and an invalid pick leaves the offer standing (the server
 * re-sends nothing - the offer is unchanged until a valid pick). Re-sent immediately
 * when a pick's banked XP rolls the *next* draft. Ephemeral, never snapshot state.
 */
export interface ArenaDraftMessage {
  kind: 'ArenaDraft';
  /** The level this draft rewards (the one just reached). */
  level: number;
  /** The seeded option list (up to ARENA_DRAFT_SIZE entries). */
  options: ArenaDraftOption[];
  /** Live run context for exact, build-aware option details. Optional for skew-safe fallback. */
  build?: {
    modifiers: Record<string, number>;
    stats: Record<string, number>;
    keystones: string[];
    ascensions: Record<string, number>;
  };
  classId?: 'fire-mage' | 'archer';
}

/**
 * Server -> client, **owner-only**: the results screen of a finished Arena run
 * (Arena Slice D) - sent once, on the tick the run ends (death or survival; a
 * disconnect forfeit has no socket to tell). The gold named here is exactly what
 * the settle banks, computed from the same frozen run tallies. Ephemeral.
 */
export interface ArenaResultsMessage {
  kind: 'ArenaResults';
  challengeId?: string;
  challengeVersion?: number;
  challengeName?: string;
  /** Retained for wire stability but **always `false`** now - the arena is endless
   * (ADR-0210), so every run ends in death; the achievement is the score. */
  survived: boolean;
  /** Ticks survived (50ms each). */
  ticks: number;
  /** Enemies killed. */
  kills: number;
  /** Gold banked for the run. */
  gold: number;
  /** The run's score (the leaderboard quantity, ADR-0172). */
  score: number;
}

/**
 * Server -> client, **broadcast to every connection** (town and arena, unlike the
 * town-only heralds): a runner just set a new all-time Arena high score (ADR-0210).
 * Ephemeral, purely a celebration trigger - each client plays the one-shot victory
 * sting (`music.playVictory`). The matching bank-herald text goes out separately on
 * the usual town-only herald path; this message carries the who/what only so a
 * client that wants to could surface it, and reaches the setter (still mid-run) and
 * any other runners the herald skips.
 */
export interface ArenaRecordMessage {
  kind: 'ArenaRecord';
  /** The record-setter's name. */
  name: string;
  /** The new all-time-high score. */
  score: number;
  /** Board partition this record belongs to (absent on legacy Standard messages). */
  challengeId?: string;
  /** Player-facing partition name for isolated announcement copy. */
  challengeName?: string;
}

/**
 * Server -> client, **owner-only**: an elite or boss just entered this run - fire
 * the entrance FX (warning banner + screen shake + audio sting). Derived directly
 * from the sim's `elite-spawn` / `boss-spawn` beat, so the *server* decides which
 * kind this is; the client no longer sniffs body ids (progressive elites, PRD:
 * 12 distinct elite bodies, and elite #12 shares the boss's Demon Knight body
 * 0x13E - body-id matching can't tell them apart, but the beat kind always can).
 * Carries the creature's display `name` so the client's overhead banner can name
 * the exact entrant ("An elite Gibberling tears into the arena!"). The elite
 * entrance is overhead-only now - no journal/system line rides alongside it; the
 * boss keeps its own herald line. Ephemeral like {@link ActionAnimationMessage}:
 * a dropped frame only misses one entrance sting.
 */
export interface ArenaWarningMessage {
  kind: 'ArenaWarning';
  /** Which entrance this is - the sim's beat kind, never a body guess. */
  warning: 'elite' | 'boss';
  /** The entity that just entered, for optional positional FX (screen shake is
   * global; carried so the client could also flash/burst at the spawn if desired). */
  entity: EntityId;
  /** The entrant's display name (from its bestiary body), for the banner text. */
  name?: string;
}

/** Messages the client sends to the server. */
export type ClientMessage =
  | LoginMessage
  | SpectateMessage
  | ClientCommandMessage
  | ArenaHistoryRequestMessage
  | WalkRequestMessage
  | DungeonMoveIntentMessage
  | SayMessage
  | SetSpeechHueMessage
  | EquipMessage
  | SetAutoEquipMessage
  | UnlockWardrobeFavoriteMessage
  | SaveWardrobeFavoriteMessage
  | OpenBinderMessage
  | RequestSoulstonesMessage
  | AttuneSoulstoneMessage
  | EquipHeroItemMessage
  | UnequipHeroItemMessage
  | AllocateHeroStatsMessage
  | RespecHeroMessage
  | TakeUpSoulstoneMessage
  | WipeSoulstoneMessage
  | ChooseStarterRecipeMessage
  | SaveCardDecksMessage
  | SelectTitleMessage
  | AdminSetNoClipMessage
  | AdminTeleportMessage
  | AdminSetSizeMessage
  | AdminEquipBascinetMessage
  | AdminSetMountMessage;

/** Messages the server sends to the client. */
export type ServerMessage =
  | WelcomeMessage
  | RejectedMessage
  | CharacterRequiredMessage
  | WorldSnapshotMessage
  | WorldKeyframeMessage
  | WorldDeltaMessage
  | MoveAckMessage
  | MoveRejectMessage
  | SpeechMessage
  | ChatHistoryRemoveMessage
  | ActionAnimationMessage
  | RatSwarmStatusMessage
  | StormStatusMessage
  | StormTelegraphMessage
  | JusticeFxMessage
  | BloodFxMessage
  | ClearCorpsesMessage
  | GoldUpdateMessage
  | CombatTextMessage
  | DungeonRunStatusMessage
  | DungeonTelegraphMessage
  | DungeonCastResultMessage
  | SoulstoneRosterMessage
  | OnlineTimeMessage
  | CollectionMessage
  | CardStateMessage
  | PackOpenedMessage
  | AutoEquipStateMessage
  | WardrobeFavoritesStateMessage
  | DuelPromptMessage
  | DuelCombatMessage
  | SpherePromptMessage
  | SphereStateMessage
  | SphereEventMessage
  | SphereCommandResultMessage
  | DiceWagerPromptMessage
  | CardDuelPromptMessage
  | CardDuelListingsMessage
  | CardDuelListingAcceptedMessage
  | CardDuelStateMessage
  | ArenaRunStatusMessage
  | ArenaDraftMessage
  | ArenaResultsMessage
  | ArenaRecordMessage
  | ArenaWarningMessage
  | ArenaFxMessage
  | ArenaHistoryPageMessage
  | ProfileStateMessage
  | AchievementAwardMessage
  | CueBatchMessage;

/** Every message that can cross the wire, in either direction. */
export type ProtocolMessage = ClientMessage | ServerMessage;

/**
 * Convert the array-of-entities persistence format into the delta-ready,
 * id-keyed wire snapshot. (Number-literal object keys are stored as strings by
 * JS, so the `Record<EntityId, ...>` round-trips through JSON unchanged.)
 */
function persistedToWire(p: PersistedWorld): WorldSnapshotMessage {
  const entities: Record<EntityId, EntitySnapshot> = {};
  for (const e of p.entities) entities[e.id] = e;
  return {
    kind: 'Snapshot',
    // Carry the server's schema version onto the wire (Issue #320) so a stale client
    // can detect a breaking deploy; falls back to the local constant for a store
    // format that predates the field.
    version: p.version ?? SCHEMA_VERSION,
    map: p.map,
    rngState: p.rngState,
    idCounter: p.idCounter,
    entities,
    commandQueue: p.commandQueue,
  };
}

/** Convert an id-keyed wire snapshot back into the persistence format. */
function wireToPersisted(s: WorldSnapshotMessage): PersistedWorld {
  return {
    // Honour the version the wire carries (Issue #320): a mismatch with the local
    // SCHEMA_VERSION makes `deserialize` throw, which the client catches and turns
    // into a reload. An older server that omits it is assumed compatible.
    version: s.version ?? SCHEMA_VERSION,
    map: s.map,
    rngState: s.rngState,
    idCounter: s.idCounter,
    entities: Object.values(s.entities),
    commandQueue: s.commandQueue,
  };
}

/**
 * Build a full {@link WorldSnapshotMessage} from a live {@link World}, reusing
 * the persistence {@link serialize}. This is what the server broadcasts each snapshot
 * frame (on the broadcast cadence, 10Hz by default since ADR-0163) and embeds in {@link WelcomeMessage}.
 *
 * Serialized in **wire shape** (`{ wire: true }`, PRD #825): each component
 * descriptor's lean broadcast form where it declares one. Today that is
 * `mobile`, which leaves its remaining walk route at home - server-side steering
 * the client never reads, but up to ~21 tiles re-sent per chaser per delta. The
 * save shape is unaffected, and both rebuild through the same descriptor.
 */
export function snapshotFromWorld(world: World): WorldSnapshotMessage {
  return persistedToWire(serialize(world, { wire: true }));
}

/**
 * Rebuild a live {@link World} from a {@link WorldSnapshotMessage}, reusing the
 * persistence {@link deserialize}. The reconstructed world is equivalent to the
 * one that produced the snapshot - same map, entities, RNG stream position, id
 * allocator, and pending commands - so it continues deterministically from the
 * same point.
 */
export function snapshotToWorld(s: WorldSnapshotMessage): World {
  return deserialize(wireToPersisted(s));
}

/**
 * Serialize a protocol message for the wire. The **three hot world messages**
 * (`Snapshot`/`Delta`/`Keyframe`) are encoded with the compact positional **binary**
 * codec (ADR-0190) and return a {@link Uint8Array}; every other, low-frequency message
 * stays plain JSON and returns a `string`. The dispatch is on `kind`, so the binary
 * path is confined to the per-tick payloads that were profiled as the dominant
 * server-tick stringify cost - the rest of the system is untouched, and both a
 * `string` and a `Uint8Array` are sent verbatim by `ws` (server) and the browser
 * `WebSocket` (client).
 */
export function encode(msg: WorldSnapshotMessage | WorldDeltaMessage | WorldKeyframeMessage): Uint8Array;
export function encode(msg: ProtocolMessage): string;
export function encode(msg: ProtocolMessage): string | Uint8Array {
  if (isWorldMessageKind(msg.kind)) {
    return encodeWorldMessage(msg as WorldSnapshotMessage | WorldDeltaMessage | WorldKeyframeMessage);
  }
  return JSON.stringify(msg);
}

/**
 * Parse a wire payload back into a protocol message. The inverse of {@link encode}: a
 * **binary** frame (a `Uint8Array`/`ArrayBuffer`/Node `Buffer`, i.e. the browser's
 * `binaryType: 'arraybuffer'` world frames) is decoded through the binary codec; a
 * `string` is JSON-parsed as before. The discriminant `kind` field then lets the
 * receiver narrow it. A malformed/incompatible binary frame throws (the caller's
 * receive guard turns that into a dropped frame, never corrupt state).
 */
export function decode(data: string | Uint8Array | ArrayBuffer): ProtocolMessage {
  if (typeof data === 'string') return JSON.parse(data) as ProtocolMessage;
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  return decodeWorldMessage(bytes);
}

/**
 * Structural deep-equality with `JSON.stringify` semantics, over the plain-JSON
 * component values `serializeEntity` builds: an object field whose value is
 * `undefined` is treated as absent (JSON omits it), an `undefined` array element
 * and a non-finite number compare as `null` (JSON serializes both so), and
 * everything else compares structurally. The contract is exactly "would the two
 * serialize to the same bytes" for the plain-JSON data snapshots are made of -
 * the comparison {@link diffEntity} has always used - without materializing the
 * bytes: the old `JSON.stringify(b) !== JSON.stringify(n)` allocated two fresh
 * strings per component per entity per baseline per broadcast, and was the
 * single hottest frame in a production CPU profile (~9% of total wall time at
 * 114 connections + 3 concurrent Arena runs, whose private worlds are diffed
 * per-run per-broadcast).
 */
function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // JSON serializes every non-finite number (NaN/Infinity) as `null`.
  const aNull = a === null || (typeof a === 'number' && !Number.isFinite(a));
  const bNull = b === null || (typeof b === 'number' && !Number.isFinite(b));
  if (aNull || bNull) return aNull && bNull;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  const aIsArray = Array.isArray(a);
  if (aIsArray !== Array.isArray(b)) return false;
  if (aIsArray) {
    const x = a as unknown[];
    const y = b as unknown[];
    if (x.length !== y.length) return false;
    for (let i = 0; i < x.length; i++) {
      // JSON serializes an undefined array element as `null`.
      if (!jsonEqual(x[i] ?? null, y[i] ?? null)) return false;
    }
    return true;
  }
  const x = a as Record<string, unknown>;
  const y = b as Record<string, unknown>;
  for (const k in x) {
    if (x[k] === undefined) continue; // JSON omits undefined-valued fields.
    if (y[k] === undefined) return false; // Absent (or undefined-valued) on the other side.
    if (!jsonEqual(x[k], y[k])) return false;
  }
  for (const k in y) {
    // A key only `y` carries (with a defined value) is a structural difference.
    if (y[k] !== undefined && x[k] === undefined) return false;
  }
  return true;
}

/**
 * Compute the **field-level** delta (ADR-0164) that turns entity snapshot `base`
 * into `next`: only the components whose serialized bytes would differ
 * ({@link jsonEqual} - `serializeEntity` builds plain-JSON component values, so
 * structural equality and byte equality coincide). Returns `null` when the two
 * are byte-identical, so a caller can drop an unchanged entity from `changed`
 * entirely.
 *
 * Generic over the component set: it walks both snapshots' actual component
 * keys, so a component neither side declares is never touched and a component
 * the codebase doesn't know about (a concurrently-added marker) diffs like any
 * other. A component in `next` but not `base` (or with different bytes) lands in
 * `set` by its full value; a component in `base` but not `next` lands in
 * `cleared`. This runs once per surviving entity per baseline per broadcast (the
 * broadcast hot path), so the walk allocates nothing until a difference is found.
 */
function diffEntity(base: EntitySnapshot, next: EntitySnapshot): EntityFieldDelta | null {
  let set: Partial<Omit<EntitySnapshot, 'id'>> | undefined;
  let cleared: EntityComponentKey[] | undefined;
  for (const key in next) {
    if (key === 'id') continue;
    const n = next[key as EntityComponentKey];
    if (n === undefined) continue; // An undefined-valued component is absent (JSON semantics).
    const b = base[key as EntityComponentKey];
    if (b === undefined || !jsonEqual(b, n)) {
      // Newly present, or its bytes changed: carry the full new value.
      ((set ??= {}) as Record<string, unknown>)[key] = n;
    }
  }
  for (const key in base) {
    if (key === 'id') continue;
    if (base[key as EntityComponentKey] === undefined) continue;
    if (next[key as EntityComponentKey] === undefined) {
      // Present on the baseline, gone now: a component removed from a surviving entity.
      (cleared ??= []).push(key as EntityComponentKey);
    }
  }
  if (set === undefined && cleared === undefined) return null;
  const delta: EntityFieldDelta = {};
  if (set !== undefined) delta.set = set;
  if (cleared !== undefined) delta.cleared = cleared;
  return delta;
}

/**
 * Rebuild the full {@link EntitySnapshot} for a `changed` entity by applying its
 * {@link EntityFieldDelta} onto the baseline entity: layer the `set` components over
 * the baseline, then drop every `cleared` component. The `id` rides forward from the
 * baseline (it is never in the delta). The inverse of {@link diffEntity}, so
 * `applyEntityDelta(base, diffEntity(base, next)!)` reproduces `next`.
 */
function applyEntityDelta(base: EntitySnapshot, delta: EntityFieldDelta): EntitySnapshot {
  const merged: EntitySnapshot = { ...base, ...delta.set };
  if (delta.cleared) {
    for (const key of delta.cleared) delete merged[key];
  }
  return merged;
}

/**
 * Compute the per-tick {@link WorldDeltaMessage} that turns `base` into `next`
 * (M9 Slice 2.2). Pure data transformation over the two id-keyed snapshots:
 *
 * - `added`: ids in `next` but not `base` (the full snapshot - a baseline for the
 *   new entity, so the client never starts an entity from a partial field-delta).
 * - `changed`: ids in both whose snapshot bytes differ, each a **field-level**
 *   {@link EntityFieldDelta} of just the changed/removed components (ADR-0164).
 * - `removed`: ids in `base` but gone from `next`.
 *
 * The tick scalars and command queue are copied from `next` in full (they are
 * tiny and change almost every tick); the static `map` is intentionally omitted
 * (see {@link WorldDeltaMessage}). The invariant the round-trip test pins is
 * `applyDelta(base, diffSnapshot(base, next))` deep-equals `next` whenever the two
 * share a map.
 */
export function diffSnapshot(
  base: WorldSnapshotMessage,
  next: WorldSnapshotMessage,
): WorldDeltaMessage {
  const added: Record<EntityId, EntitySnapshot> = {};
  const changed: Record<EntityId, EntityFieldDelta> = {};
  const removed: EntityId[] = [];

  for (const key of Object.keys(next.entities)) {
    const id = key as unknown as EntityId;
    const n = next.entities[id]!;
    const b = base.entities[id];
    if (b === undefined) {
      added[id] = n;
    } else {
      const fieldDelta = diffEntity(b, n);
      if (fieldDelta !== null) changed[id] = fieldDelta;
    }
  }
  for (const key of Object.keys(base.entities)) {
    const id = key as unknown as EntityId;
    if (!(key in next.entities)) removed.push(id);
  }

  return {
    kind: 'Delta',
    rngState: next.rngState,
    idCounter: next.idCounter,
    added,
    changed,
    removed,
    commandQueue: next.commandQueue,
  };
}

/**
 * Apply a {@link WorldDeltaMessage} to a baseline {@link WorldSnapshotMessage},
 * producing the full snapshot for this tick (M9 Slice 2.2). The inverse of
 * {@link diffSnapshot}: start from the baseline's entities, drop the `removed`
 * ids, layer full `added` snapshots in, then **merge** each `changed` entity's
 * field-level {@link EntityFieldDelta} onto the baseline entity it holds (ADR-0164);
 * carry the static `map` forward from the baseline and take the tick scalars +
 * command queue from the delta. The result is a normal full snapshot the existing
 * {@link snapshotToWorld} can rebuild a world from, so the read-model has exactly
 * one reconstruction path whether it was fed a keyframe or a delta.
 *
 * A `changed` entry names an entity the baseline is guaranteed to hold (diffSnapshot
 * only classifies an id as `changed` when it is present in both), so the field-merge
 * always has a base to layer onto; a client kept honest by the periodic keyframe
 * resync never applies a field-delta against a missing baseline entity.
 */
export function applyDelta(
  base: WorldSnapshotMessage,
  delta: WorldDeltaMessage,
): WorldSnapshotMessage {
  const entities: Record<EntityId, EntitySnapshot> = { ...base.entities };
  for (const id of delta.removed) delete entities[id];
  for (const key of Object.keys(delta.added)) {
    const id = key as unknown as EntityId;
    entities[id] = delta.added[id]!;
  }
  for (const key of Object.keys(delta.changed)) {
    const id = key as unknown as EntityId;
    const baseEntity = entities[id];
    // Defensive: a field-delta for an entity the baseline lacks can only happen on a
    // corrupt/reordered stream (the periodic keyframe resync is the recovery path);
    // skip it rather than synthesize an id-less partial entity.
    if (baseEntity === undefined) continue;
    entities[id] = applyEntityDelta(baseEntity, delta.changed[id]!);
  }

  return {
    kind: 'Snapshot',
    // The schema version is immutable within a connection, so carry it forward from
    // the baseline (Issue #320) - deltas don't restate it - keeping a delta-driven
    // read-model byte-identical to a full-snapshot-driven one.
    version: base.version,
    map: base.map,
    rngState: delta.rngState,
    idCounter: delta.idCounter,
    entities,
    commandQueue: delta.commandQueue,
  };
}

/**
 * Strip the static `map` off a full {@link WorldSnapshotMessage} to produce the
 * map-less {@link WorldKeyframeMessage} the server broadcasts as its periodic
 * resync (issue #119). Everything else - the complete entity set, the tick
 * scalars, the command queue - rides verbatim; only the immutable map (which the
 * client already holds from connect) is dropped. The `entities` Record is shared
 * by reference, not copied: the caller built it for this tick and does not mutate
 * it, exactly as {@link diffSnapshot} shares the snapshots it reads.
 */
export function keyframeFromSnapshot(full: WorldSnapshotMessage): WorldKeyframeMessage {
  return {
    kind: 'Keyframe',
    rngState: full.rngState,
    idCounter: full.idCounter,
    entities: full.entities,
    commandQueue: full.commandQueue,
  };
}

/**
 * Apply a map-less {@link WorldKeyframeMessage} onto a baseline
 * {@link WorldSnapshotMessage}, producing this tick's full snapshot (issue #119).
 * The keyframe carries the **complete** entity set, so - unlike {@link applyDelta} -
 * the baseline's entities are replaced wholesale (the keyframe is authoritative and
 * self-correcting); only the static `map` is carried forward from the baseline, and
 * the tick scalars + command queue come from the keyframe. The result is a normal
 * full snapshot {@link snapshotToWorld} can rebuild a world from, so a keyframe-driven
 * read-model is byte-identical to a full-snapshot-driven one. The round-trip the
 * tests pin is `applyKeyframe(base, keyframeFromSnapshot(next))` deep-equals `next`
 * whenever the two share a map.
 */
export function applyKeyframe(
  base: WorldSnapshotMessage,
  keyframe: WorldKeyframeMessage,
): WorldSnapshotMessage {
  return {
    kind: 'Snapshot',
    // Carry the schema version forward from the baseline (Issue #320); the map-less
    // keyframe, like a delta, doesn't restate it.
    version: base.version,
    map: base.map,
    rngState: keyframe.rngState,
    idCounter: keyframe.idCounter,
    entities: keyframe.entities,
    commandQueue: keyframe.commandQueue,
  };
}
